<template>
  <div v-if="salesOrder">
    <b-row class="content-header sales_ch_custom_header">
      <b-col
        class="content-header-left mb-2"
        cols="12"
        md="12"
      >
        <b-row class="breadcrumbs-top align-items-center">
          <b-col
            cols="12"
            md="3"
          >
            <h1 class="custom-header-title">
              {{ salesOrder.stringID }} - {{ salesOrder.customerName }}
            </h1>
            <p class="log_info">
              Submitted on {{ dateFormatWithTime(salesOrder.createdAt) }} <br>Last updated on {{ dateFormatWithTime(salesOrder.updatedAt) }}
            </p>
          </b-col>
          <b-col
            cols="12"
            md="9"
            class="content-header-right text-md-right mb-1"
          >
            <div class="user_block ac_type file_creation">
              <div class="user_avatar" />
              <div class="user_info">
                <h6>SCLC File No.</h6>
                <span class="text-muted-999">
                  <feather-icon
                    icon="LoaderIcon"
                    class="text-primary"
                  /> Creating file...
                </span>
              </div>
            </div>
            <div class="user_block pr_stage_type user__block-margin-adjust">
              <div class="user_avatar" />
              <div class="user_info">
                <h6>Pending Dept.</h6>
                <span
                  class="font-weight-bold"
                >{{ resolveAcronymName(salesOrder.pendingDepartment) }}</span>
              </div>
            </div>
            <div class="user_block ac_type sign_shape">
              <div class="user_avatar" />
              <div class="user_info">
                <h6>Status</h6>
                <span class="font-weight-bold text-capitalize">{{ workflowStatusLabel }}</span>
              </div>
            </div>
            <div class="user_block file_payment_type user__block-margin-adjust">
              <div class="user_avatar" />
              <div class="user_info">
                <h6>Payment Status</h6>
                <span class="font-weight-bold text-capitalize">{{ resolvePaymentStatusLabel(paymentStatus) }}</span>
              </div>
            </div>
          </b-col>
        </b-row>
      </b-col>
    </b-row>

    <!-- Workflow chrome (stepper/banner/toolbar) - wired to the real backend workflow. -->
    <div class="so-workflow-bar workflow_wrapper">
      <div class="so-stepper">
        <div
          v-for="(step, index) in workflowSteps"
          :key="step.key"
          class="so-step"
          :class="{ active: index === currentStepIndex, done: index < currentStepIndex, disabled: isStageDisabled(index) }"
        >
          <div class="stepSingle">
            <span class="so-step-index">{{ index + 1 }}</span>
            <span class="so-step-label">{{ step.label }}</span>
            <span class="so-step-icons">
              <feather-icon
                v-for="stepNum in stepsForStage(index)"
                :key="stepNum"
                v-b-tooltip.hover="stepIconStatus(stepNum) === 'disabled' ? null : { html: true, title: stepIconTooltip(stepNum) }"
                :icon="stepIconStatus(stepNum) === 'completed' ? 'CheckIcon' : 'FeatherIcon'"
                :class="['so-step-icon', `step-icon-${stepIconStatus(stepNum)}`]"
              />
            </span>
            <feather-icon
              v-if="index < workflowSteps.length - 1"
              icon="ChevronRightIcon"
              class="so-step-chevron"
            />
          </div>
        </div>
      </div>

      <div
        v-if="verificationMessage"
        class="so-verification-banner"
      >
        {{ verificationMessage }}
      </div>

      <div class="so-toolbar">
        <div class="so-toolbar-cluster">
          <input
            ref="attachmentInput"
            type="file"
            multiple
            class="d-none"
            @change="onAttachmentFileSelected"
          >
          <b-button
            variant="primary"
            @click="triggerAttachmentUpload"
          >
            <feather-icon icon="UploadIcon" />
          </b-button>
          <b-button
            v-if="pendingAttachmentFiles.length"
            variant="success"
            :disabled="uploadingAttachment"
            @click="saveAttachments"
          >
            Save
          </b-button>
          <b-button
            variant="primary"
            @click="focusCommentBox"
          >
            <feather-icon icon="MessageSquareIcon" />
          </b-button>
          <b-button
            v-if="isReviewingDept"
            variant="primary"
            class="btn_refresh_os"
            :disabled="revertingStep || !earlierApplicableSteps.length"
            @click="revertStep"
          >
            <feather-icon icon="RotateCcwIcon" />
          </b-button>
          <span
            v-if="isStepOwnerDept"
            :key="`reassign-${salesOrder.currentStep}`"
            v-b-tooltip.hover="!canViewThisAction('reassign-department', 'SalesOrder') ? 'You do not have permission to re-assign this step' : null"
            class="d-inline-block"
            tabindex="0"
          >
            <b-button
              variant="primary"
              :disabled="assigningBack || !canViewThisAction('reassign-department', 'SalesOrder')"
              :style="(assigningBack || !canViewThisAction('reassign-department', 'SalesOrder')) ? 'pointer-events: none;' : null"
              @click="assignBackToDept"
            >
              <feather-icon
                icon="UserIcon"
                class="mr-50"
              />
              Re-assign
            </b-button>
          </span>
        </div>
        <div class="so-toolbar-cluster so_tb_sl_right">
          <b-button
            v-if="canReassignBackToOwner"
            variant="primary"
            :disabled="reassigningBackToOwner"
            @click="reassignBackToOwner"
          >
            <feather-icon
              icon="CornerUpLeftIcon"
              class="mr-50"
            />
            Re-assign back to {{ (salesOrder.stepDepartments && salesOrder.stepDepartments[salesOrder.currentStep]) || 'owner' }}
          </b-button>
          <b-button
            v-if="canActOnUnownedStep && !isActionRequired"
            variant="primary"
            :disabled="sendingBack"
            @click="sendBackToSalesAgent"
          >
            <feather-icon
              icon="SendIcon"
              class="mr-50"
            />
            Send back to Sales Agent
          </b-button>
          <span
            v-if="isStepOwnerDept && !isActionRequired"
            :key="`complete-step-${salesOrder.currentDepartmentOverride || 'none'}`"
            v-b-tooltip.hover="salesOrder.currentDepartmentOverride ? 'To complete this step, re-assign the SO back to your department' : null"
            class="d-inline-block"
            tabindex="0"
          >
            <b-button
              variant="success"
              :disabled="completingStep || !!salesOrder.currentDepartmentOverride"
              :style="(completingStep || !!salesOrder.currentDepartmentOverride) ? 'pointer-events: none;' : null"
              @click="completeStep"
            >
              <feather-icon
                icon="CheckIcon"
                class="mr-50"
              />
              Complete this step
            </b-button>
          </span>
        </div>
      </div>
    </div>

    <div class="submission-detail-body position-relative submission_dt_wrapper_so">
      <h4 class="submission-item-title mb-1">
        {{ salesOrder.stringID }} - {{ salesOrder.productType }} {{ salesOrder.lotNo ? `${salesOrder.zoneNo}-${salesOrder.lotNo}` : salesOrder.packageName }}
      </h4>

      <b-tabs
        pills
        class="dashboard__tabs submission-detail-tabs"
      >
        <b-tab>
          <template #title>
            <b-card
              no-body
              class="dashboard__tab-card"
            >
              <b-card-body class="d-flex align-items-center">
                <b-avatar
                  variant="light-primary"
                  size="40"
                  rounded
                >
                  <feather-icon
                    size="20"
                    icon="UserIcon"
                  />
                </b-avatar>
                <div class="truncate ml-2">
                  <h2>Agent Details</h2>
                </div>
              </b-card-body>
            </b-card>
          </template>

          <b-card
            no-body
            class="dashboard__tab-content-card"
          >
            <b-tabs class="purchaser-tabs mb-2">
              <b-tab
                title="Sales Agent"
                active
              >
                <agent-detail-card
                  v-if="salesAgentDetail"
                  :agent-detail="salesAgentDetail"
                  :agent-type="salesAgentType"
                  role-label="Sales Agent"
                  permission-resource="SalesOrder"
                  permission-action="change-agent"
                  @change-agent="showSalesAgentModal = true"
                />
                <span v-else>No sales agent on this order</span>
              </b-tab>
              <b-tab title="Service Agent">
                <agent-detail-card
                  v-if="salesOrder.serviceAgent"
                  :agent-detail="salesOrder.serviceAgent"
                  agent-type="Agent"
                  role-label="Service Agent"
                  permission-resource="SalesOrder"
                  permission-action="change-agent"
                  @change-agent="showServiceAgentModal = true"
                />
                <span v-else>No service agent on this order</span>
              </b-tab>
            </b-tabs>
          </b-card>
        </b-tab>

        <b-tab>
          <template #title>
            <b-card
              no-body
              class="dashboard__tab-card"
            >
              <b-card-body class="d-flex align-items-center">
                <b-avatar
                  variant="light-primary"
                  size="40"
                  rounded
                >
                  <feather-icon
                    size="20"
                    icon="InfoIcon"
                  />
                </b-avatar>
                <div class="truncate ml-2">
                  <h2>Order Information</h2>
                </div>
              </b-card-body>
            </b-card>
          </template>

          <b-card
            no-body
            class="dashboard__tab-content-card"
          >
            <b-row class="match-height">
              <b-col
                cols="12"
                md="6"
              >
                <b-card class="has-padding order-info-card">
                  <div class="d-flex justify-content-between align-items-center mb-2">
                    <b-card-title class="mb-0">
                      Purchaser Details
                    </b-card-title>
                    <b-button
                      v-if="canViewThisAction('edit-purchaser-details', 'SalesOrder')"
                      variant="flat-primary"
                      @click="showPurchaserDetailsModal = true"
                    >
                      <feather-icon icon="EditIcon" /> Edit
                    </b-button>
                  </div>
                  <b-table-simple
                    borderless
                    responsive
                    class="particulars_of_deceased d-none d-md-block"
                  >
                    <b-tbody>
                      <b-tr>
                        <b-th width="230px">
                          Purchaser Name
                        </b-th>
                        <b-td>
                          <b-button
                            v-if="canViewThisAction('show', 'Customer') && salesOrder.customer"
                            v-ripple.400="'rgba(30, 30, 30, 0.15)'"
                            variant="link"
                            class="p-0 weight-600 align-middle"
                            :to="{ name: 'customers-customers-show', params: { id: salesOrder.customer._id } }"
                          >
                            {{ salesOrder.customerName || '–' }}
                          </b-button>
                          <span v-else>{{ salesOrder.customerName || '–' }}</span>
                        </b-td>
                      </b-tr>
                      <b-tr>
                        <b-th width="230px">
                          NRIC
                        </b-th>
                        <b-td>{{ salesOrder.customerNric || '–' }}</b-td>
                      </b-tr>
                      <b-tr>
                        <b-th width="230px">
                          Date of Birth
                        </b-th>
                        <b-td>{{ salesOrder.customerDob ? dateFormat(salesOrder.customerDob) : '–' }}</b-td>
                      </b-tr>
                      <b-tr>
                        <b-th width="230px">
                          Sex
                        </b-th>
                        <b-td>{{ salesOrder.customerSex || '–' }}</b-td>
                      </b-tr>
                      <b-tr>
                        <b-th width="230px">
                          Race
                        </b-th>
                        <b-td>{{ salesOrder.customerRace || '–' }}</b-td>
                      </b-tr>
                      <b-tr>
                        <b-th width="230px">
                          Nationality
                        </b-th>
                        <b-td>{{ salesOrder.customerNationality || '–' }}</b-td>
                      </b-tr>
                      <b-tr>
                        <b-th width="230px">
                          Mobile No.
                        </b-th>
                        <b-td>{{ salesOrder.customerContact || '–' }}</b-td>
                      </b-tr>
                      <b-tr>
                        <b-th width="230px">
                          Email Address
                        </b-th>
                        <b-td>{{ salesOrder.customerEmail || '–' }}</b-td>
                      </b-tr>
                      <b-tr>
                        <b-th width="230px">
                          Permanent Address
                        </b-th>
                        <b-td>{{ permanentAddress || '–' }}</b-td>
                      </b-tr>
                      <b-tr>
                        <b-th width="230px">
                          Correspondence Address
                        </b-th>
                        <b-td>{{ correspondenceAddress || '–' }}</b-td>
                      </b-tr>
                    </b-tbody>
                  </b-table-simple>
                </b-card>
              </b-col>

              <b-col
                cols="12"
                md="6"
              >
                <b-card class="has-padding order-info-card">
                  <b-card-title class="mb-2">
                    Order Item Details
                  </b-card-title>
                  <b-table-simple
                    borderless
                    responsive
                    class="particulars_of_deceased d-none d-md-block"
                  >
                    <b-tbody>
                      <b-tr>
                        <b-th width="230px">
                          Product Type
                        </b-th>
                        <b-td>{{ salesOrder.productType || '–' }}</b-td>
                      </b-tr>
                      <template v-if="salesOrder.lotNo">
                        <b-tr>
                          <b-th width="230px">
                            Zone No.
                          </b-th>
                          <b-td>{{ salesOrder.zoneNo || '–' }}</b-td>
                        </b-tr>
                        <b-tr>
                          <b-th width="230px">
                            Lot No.
                          </b-th>
                          <b-td>{{ salesOrder.lotNo || '–' }}</b-td>
                        </b-tr>
                        <b-tr>
                          <b-th width="230px">
                            Lot Type
                          </b-th>
                          <b-td>{{ salesOrder.lotType || '–' }}</b-td>
                        </b-tr>
                        <b-tr>
                          <b-th width="230px">
                            Bury Capacity
                          </b-th>
                          <b-td>{{ salesOrder.lot && salesOrder.lot.buryCapacity ? salesOrder.lot.buryCapacity : '–' }}</b-td>
                        </b-tr>
                        <b-tr>
                          <b-th width="230px">
                            Area Length
                          </b-th>
                          <b-td>{{ salesOrder.lot && salesOrder.lot.size ? salesOrder.lot.size : '–' }}</b-td>
                        </b-tr>
                        <b-tr>
                          <b-th width="230px">
                            Direction
                          </b-th>
                          <b-td>{{ salesOrder.lot && salesOrder.lot.direction ? salesOrder.lot.direction : '–' }}</b-td>
                        </b-tr>
                        <b-tr>
                          <b-th width="230px">
                            Level No.
                          </b-th>
                          <b-td>{{ salesOrder.lot && salesOrder.lot.levelNo ? salesOrder.lot.levelNo : '–' }}</b-td>
                        </b-tr>
                        <b-tr>
                          <b-th width="230px">
                            Section No.
                          </b-th>
                          <b-td>{{ salesOrder.lot && salesOrder.lot.sectionNo ? salesOrder.lot.sectionNo : '–' }}</b-td>
                        </b-tr>
                        <b-tr>
                          <b-th width="230px">
                            Earliest Interment Date
                          </b-th>
                          <b-td>{{ salesOrder.lot && salesOrder.lot.intermentDate ? dateFormat(salesOrder.lot.intermentDate) : 'Immediate' }}</b-td>
                        </b-tr>
                      </template>
                      <b-tr v-else>
                        <b-th width="230px">
                          Package
                        </b-th>
                        <b-td>{{ salesOrder.packageName || '–' }}</b-td>
                      </b-tr>
                      <b-tr>
                        <b-th width="230px">
                          Need Type
                        </b-th>
                        <b-td>{{ needTypeLabel(salesOrder.needType) }}</b-td>
                      </b-tr>
                    </b-tbody>
                  </b-table-simple>
                </b-card>
              </b-col>
            </b-row>
          </b-card>
        </b-tab>

        <b-tab>
          <template #title>
            <b-card
              no-body
              class="dashboard__tab-card"
            >
              <b-card-body class="d-flex align-items-center">
                <b-avatar
                  variant="light-primary"
                  size="40"
                  rounded
                >
                  <feather-icon
                    size="20"
                    icon="DollarSignIcon"
                  />
                </b-avatar>
                <div class="truncate ml-2">
                  <h2>Payment Information</h2>
                </div>
              </b-card-body>
            </b-card>
          </template>

          <b-card
            no-body
            class="dashboard__tab-content-card"
          >
            <b-row class="match-height payment-info-top-row">
              <b-col
                cols="12"
                md="4"
              >
                <b-card class="has-padding">
                  <b-card-title class="mb-2">
                    Purchase Option
                  </b-card-title>
                  <b-table-simple
                    borderless
                    responsive
                    class="particulars_of_deceased d-none d-md-block"
                  >
                    <b-tbody>
                      <b-tr>
                        <b-th width="150px">
                          Purchase Option
                        </b-th>
                        <b-td>{{ purchaseOptionLabel(salesOrder.paymentType) }}</b-td>
                      </b-tr>
                      <b-tr v-if="salesOrder.paymentType === 'downpayment'">
                        <b-th
                          width="150px"
                          :class="{ 'text-danger': isDownpaymentBelowMinimum }"
                        >
                          Downpayment
                        </b-th>
                        <b-td :class="{ 'text-danger': isDownpaymentBelowMinimum }">
                          {{ salesOrder.downPaymentPercentage }}%
                          <feather-icon
                            v-if="isDownpaymentBelowMinimum"
                            v-b-tooltip.hover="`For ${pluralizedProductType}, the minimum downpayment is ${matchedTenureTier.minDownPaymentPercentage}%`"
                            icon="InfoIcon"
                            size="14"
                            class="ml-25"
                          />
                        </b-td>
                      </b-tr>
                    </b-tbody>
                  </b-table-simple>
                </b-card>

                <b-card class="has-padding mb-0">
                  <b-card-title class="mb-2 d-flex justify-content-between align-items-center">
                    <span>Discounts</span>
                    <b-button
                      v-if="canViewThisAction('edit-discounts', 'SalesOrder')"
                      variant="flat-primary"
                      size="sm"
                      @click="showPromotionsModal = true"
                    >
                      <feather-icon icon="EditIcon" /> Edit
                    </b-button>
                  </b-card-title>
                  <b-table-simple
                    borderless
                    responsive
                  >
                    <b-tbody>
                      <b-tr>
                        <b-td class="font-weight-bold">
                          Discount Type
                        </b-td>
                        <b-td class="font-weight-bold">
                          Discount Amount
                        </b-td>
                      </b-tr>
                      <b-tr
                        v-for="promotion in salesOrder.promotions"
                        :key="promotion._id"
                      >
                        <b-td>
                          <div class="text-primary">
                            {{ promotion.name }}
                          </div>
                          <small>{{ promotion.couponCode }}</small>
                        </b-td>
                        <b-td>
                          <div>-${{ numberFormat(promotion.amount) }}</div>
                          <small :class="promotion.requiredBdVerification ? 'text-warning' : 'text-muted'">
                            {{ promotion.requiredBdVerification ? 'Verification needed' : 'Auto-applied' }}
                          </small>
                        </b-td>
                      </b-tr>
                      <b-tr v-if="!salesOrder.promotions || !salesOrder.promotions.length">
                        <b-td colspan="2">
                          No discounts applied
                        </b-td>
                      </b-tr>
                    </b-tbody>
                  </b-table-simple>
                </b-card>
              </b-col>

              <b-col
                cols="12"
                md="4"
              >
                <b-card class="has-padding h-100 mb-0">
                  <b-card-title class="mb-2">
                    Price Details
                  </b-card-title>
                  <b-table-simple
                    borderless
                    responsive
                    class="particulars_of_deceased d-none d-md-block"
                  >
                    <b-tbody>
                      <b-tr v-if="['Funeral Service Package', 'Urn'].includes(salesOrder.productType)">
                        <b-th width="150px">
                          Product Price
                        </b-th>
                        <b-td>${{ numberFormat(salesOrder.productPrice) }}</b-td>
                      </b-tr>
                      <template v-else>
                        <b-tr>
                          <b-th width="150px">
                            Lot Price
                          </b-th>
                          <b-td>${{ numberFormat(salesOrder.productPrice) }}</b-td>
                        </b-tr>
                        <b-tr>
                          <b-th width="150px">
                            Maintenance Fee
                          </b-th>
                          <b-td>${{ numberFormat(salesOrder.maintenanceFee) }}</b-td>
                        </b-tr>
                      </template>
                      <b-tr
                        v-for="promotion in salesOrder.promotions"
                        :key="promotion._id"
                      >
                        <b-th width="150px">
                          {{ promotion.name }}
                        </b-th>
                        <b-td>-${{ numberFormat(promotion.amount) }}</b-td>
                      </b-tr>
                      <b-tr>
                        <b-td
                          colspan="2"
                          class="p-0"
                        >
                          <hr class="my-50">
                        </b-td>
                      </b-tr>
                      <b-tr>
                        <b-th width="150px">
                          Subtotal
                        </b-th>
                        <b-td>${{ numberFormat(orderSubtotal) }}</b-td>
                      </b-tr>
                      <b-tr>
                        <b-th width="150px">
                          GST {{ salesOrder.gstPercentage }}%
                        </b-th>
                        <b-td>${{ numberFormat(salesOrder.gstAmount) }}</b-td>
                      </b-tr>
                      <b-tr>
                        <b-td
                          colspan="2"
                          class="p-0"
                        >
                          <hr class="my-50">
                        </b-td>
                      </b-tr>
                      <b-tr>
                        <b-th width="150px">
                          Order Total
                        </b-th>
                        <b-td>${{ numberFormat(orderTotal) }}</b-td>
                      </b-tr>
                      <b-tr v-if="salesOrder.paymentType === 'downpayment'">
                        <b-th width="150px">
                          Downpayment
                        </b-th>
                        <b-td>${{ numberFormat(salesOrder.downPayment) }}</b-td>
                      </b-tr>
                      <b-tr v-if="salesOrder.paymentType === 'downpayment'">
                        <b-th width="150px">
                          Balance
                        </b-th>
                        <b-td>${{ numberFormat(orderSubtotal - salesOrder.downPayment) }}</b-td>
                      </b-tr>
                    </b-tbody>
                  </b-table-simple>
                </b-card>
              </b-col>

              <b-col
                cols="12"
                md="4"
              >
                <b-card class="has-padding h-100 mb-0">
                  <b-card-title class="mb-2 d-flex justify-content-between align-items-center">
                    <span>Installment Details</span>
                    <b-button
                      v-if="salesOrder.paymentType === 'downpayment' && canViewThisAction('edit-installment', 'SalesOrder')"
                      variant="flat-primary"
                      size="sm"
                      @click="showInstallmentModal = true"
                    >
                      <feather-icon icon="EditIcon" /> Edit
                    </b-button>
                  </b-card-title>
                  <p
                    v-if="salesOrder.paymentType !== 'downpayment'"
                    class="text-muted mb-0"
                  >
                    No installment plan for this order
                  </p>
                  <b-table-simple
                    v-else
                    borderless
                    responsive
                    class="particulars_of_deceased d-none d-md-block"
                  >
                    <b-tbody>
                      <b-tr>
                        <b-th width="170px">
                          Installment Amount
                        </b-th>
                        <b-td>${{ numberFormat(salesOrder.installmentAmount) }}</b-td>
                      </b-tr>
                      <b-tr>
                        <b-th
                          width="170px"
                          :class="{ 'text-danger': isTenureAboveMaximum }"
                        >
                          Tenure
                        </b-th>
                        <b-td :class="{ 'text-danger': isTenureAboveMaximum }">
                          {{ salesOrder.installmentTenure }} months
                          <feather-icon
                            v-if="isTenureAboveMaximum"
                            v-b-tooltip.hover="`For ${pluralizedProductType}, the maximum tenure is ${matchedTenureTier.maxTenureMonths} months`"
                            icon="InfoIcon"
                            size="14"
                            class="ml-25"
                          />
                        </b-td>
                      </b-tr>
                      <b-tr>
                        <b-th width="170px">
                          Mode of Payment
                        </b-th>
                        <b-td>{{ salesOrder.installmentMode || '–' }}</b-td>
                      </b-tr>
                      <b-tr>
                        <b-td
                          colspan="2"
                          class="p-0"
                        >
                          <hr class="my-50">
                        </b-td>
                      </b-tr>
                      <b-tr>
                        <b-th width="170px">
                          Installment Due Date
                        </b-th>
                        <b-td>{{ salesOrder.installmentDueDate ? dateFormat(salesOrder.installmentDueDate) : '–' }}</b-td>
                      </b-tr>
                      <b-tr>
                        <b-th width="170px">
                          Monthly Installment
                        </b-th>
                        <b-td>${{ numberFormat(salesOrder.monthlyInstallment) }}</b-td>
                      </b-tr>
                      <b-tr>
                        <b-th width="170px">
                          Final Installment
                        </b-th>
                        <b-td>${{ numberFormat(salesOrder.finalInstallment) }}</b-td>
                      </b-tr>
                    </b-tbody>
                  </b-table-simple>
                </b-card>
              </b-col>
            </b-row>

            <b-row>
              <b-col cols="12">
                <b-card
                  no-body
                  class="has-padding mt-2 tab-bottom-card"
                >
                  <b-card-body>
                    <b-card-title class="mb-2">
                      Payment Information
                    </b-card-title>
                    <b-table-simple
                      responsive
                      class="payment-history-table"
                    >
                      <b-thead>
                        <b-tr>
                          <b-th>Trx No.</b-th>
                          <b-th>Payment Mode</b-th>
                          <b-th>Amount</b-th>
                          <b-th>Payment Reference</b-th>
                          <b-th>Date/Time</b-th>
                          <b-th>Processed By</b-th>
                        </b-tr>
                      </b-thead>
                      <b-tbody>
                        <b-tr v-if="salesOrder.paidAt">
                          <b-td>{{ salesOrder.trxNo || '–' }}</b-td>
                          <b-td>{{ salesOrder.paymentMethod || '–' }}</b-td>
                          <b-td>${{ numberFormat(salesOrder.amountPaid) }}</b-td>
                          <b-td>{{ salesOrder.chargeID || '–' }}</b-td>
                          <b-td>{{ salesOrder.paidAt ? dateFormatWithTime(salesOrder.paidAt) : '–' }}</b-td>
                          <b-td>
                            <b-button
                              v-if="salesOrder.payment && salesOrder.payment.processedBy && canViewThisAction('show', 'User')"
                              variant="link"
                              class="p-0 weight-600 align-middle"
                              :to="{ name: 'directory-users-show', params: { id: salesOrder.payment.processedBy._id } }"
                            >
                              {{ salesOrder.payment.processedBy.name }}
                            </b-button>
                            <span v-else>{{ salesOrder.payment && salesOrder.payment.processedBy ? salesOrder.payment.processedBy.name : '–' }}</span>
                          </b-td>
                        </b-tr>
                        <b-tr v-else>
                          <b-td colspan="6">
                            <div class="mt-1">
                              No payments recorded yet
                            </div>
                          </b-td>
                        </b-tr>
                      </b-tbody>
                    </b-table-simple>
                  </b-card-body>
                </b-card>
              </b-col>
            </b-row>
          </b-card>
        </b-tab>

        <b-tab>
          <template #title>
            <b-card
              no-body
              class="dashboard__tab-card"
            >
              <b-card-body class="d-flex align-items-center">
                <b-avatar
                  variant="light-primary"
                  size="40"
                  rounded
                >
                  <feather-icon
                    size="20"
                    icon="FileTextIcon"
                  />
                </b-avatar>
                <div class="truncate ml-2">
                  <h2>Information & Documents</h2>
                </div>
              </b-card-body>
            </b-card>
          </template>

          <b-card
            no-body
            class="dashboard__tab-content-card"
          >
            <b-row class="match-height payment-info-top-row">
              <b-col
                cols="12"
                md="6"
              >
                <b-card class="has-padding h-100 mb-0">
                  <b-card-title class="mb-2 d-flex justify-content-between align-items-center">
                    <span>Required Information</span>
                    <b-button
                      v-if="canViewThisAction('edit-required-information', 'SalesOrder')"
                      variant="flat-primary"
                      size="sm"
                      @click="showRequiredInfoModal = true"
                    >
                      <feather-icon icon="EditIcon" /> Edit
                    </b-button>
                  </b-card-title>
                  <b-table-simple
                    borderless
                    responsive
                    class="particulars_of_deceased d-none d-md-block"
                  >
                    <b-tbody>
                      <b-tr v-if="salesOrder.productType === 'Little Buddha'">
                        <b-th width="230px">
                          End Users
                        </b-th>
                        <b-td>
                          <span v-if="salesOrder.endUsers && salesOrder.endUsers.length">
                            <span
                              v-for="(user, index) in salesOrder.endUsers"
                              :key="user._id"
                            >#{{ index + 1 }}. {{ user.name }}<span v-if="index < salesOrder.endUsers.length - 1">, </span></span>
                          </span>
                          <span v-else>–</span>
                        </b-td>
                      </b-tr>
                      <b-tr>
                        <b-th width="230px">
                          Personal Data Consent
                        </b-th>
                        <b-td>{{ salesOrder.personalDataConsent ? 'Yes, I agree' : 'No' }}</b-td>
                      </b-tr>
                      <template v-if="salesOrder.productType !== 'Little Buddha'">
                        <b-tr>
                          <b-th width="230px">
                            Purchase Declaration
                          </b-th>
                          <b-td>{{ salesOrder.purchaseDeclaration ? 'Yes, I agree' : 'No' }}</b-td>
                        </b-tr>
                        <b-tr>
                          <b-th width="230px">
                            Agent Self-Purchase Declaration
                          </b-th>
                          <b-td>{{ salesOrder.agentSelfPurchase ? 'Yes, I agree' : 'No' }}</b-td>
                        </b-tr>
                      </template>
                      <b-tr>
                        <b-th width="230px">
                          Marketing Consent
                        </b-th>
                        <b-td>{{ salesOrder.marketingDataConsent ? 'I want to receive advertising and promotional material' : 'No' }}</b-td>
                      </b-tr>
                      <b-tr v-if="salesOrder.productType === 'Little Buddha'">
                        <b-th width="230px">
                          Worship Period
                        </b-th>
                        <b-td>{{ salesOrder.worshipPeriodStart && salesOrder.worshipPeriodEnd ? `${dateFormat(salesOrder.worshipPeriodStart)} - ${dateFormat(salesOrder.worshipPeriodEnd)}` : '–' }}</b-td>
                      </b-tr>
                    </b-tbody>
                  </b-table-simple>
                </b-card>
              </b-col>

              <b-col
                cols="12"
                md="6"
              >
                <b-card class="has-padding h-100 mb-0">
                  <b-card-title class="mb-2">
                    Related Nominees
                  </b-card-title>
                  <b-tabs class="purchaser-tabs">
                    <b-tab :title="`Intended User (${(salesOrder.intendedUsers || []).length})`">
                      <b-row
                        v-if="salesOrder.intendedUsers && salesOrder.intendedUsers.length"
                        class="match-height"
                      >
                        <b-col
                          v-for="(user, index) in salesOrder.intendedUsers"
                          :key="user._id"
                          md="6"
                        >
                          <b-card
                            class="card-transaction iu-card"
                            no-body
                          >
                            <b-card-header class="no-bottom-border">
                              <b-card-title>Intended User #{{ index + 1 }}</b-card-title>
                              <b-button
                                variant="none"
                                class="btn-icon rounded-circle ml-auto mr-1 flat-primary-bg"
                              >
                                <feather-icon icon="UserIcon" />
                              </b-button>
                              <b-dropdown
                                v-if="canViewThisAction('edit-nominee', 'SalesOrder') || canViewThisAction('nominee-delete', 'SalesOrder')"
                                variant="link"
                                no-caret
                                class="chart-dropdown"
                                toggle-class="p-0"
                                right
                              >
                                <template #button-content>
                                  <feather-icon
                                    icon="MoreVerticalIcon"
                                    size="18"
                                    class="text-body cursor-pointer"
                                  />
                                </template>
                                <b-dropdown-item
                                  v-if="canViewThisAction('edit-nominee', 'SalesOrder')"
                                  @click="openIntendedUserModal(user)"
                                >
                                  <feather-icon icon="EditIcon" />
                                  <span class="align-middle ml-50">Edit</span>
                                </b-dropdown-item>
                                <b-dropdown-item
                                  v-if="canViewThisAction('nominee-delete', 'SalesOrder')"
                                  @click="confirmRemoveIntendedUser(user)"
                                >
                                  <feather-icon icon="Trash2Icon" />
                                  <span class="align-middle ml-50">Remove</span>
                                </b-dropdown-item>
                              </b-dropdown>
                            </b-card-header>
                            <b-card-body>
                              <span class="text-bold-black align-middle text-uppercase">
                                {{ user.name || '–' }}
                              </span>
                              <b-badge
                                v-if="user.isPurchaser"
                                variant="light-info"
                                class="ml-50"
                              >
                                Purchaser
                              </b-badge>
                              <br>
                              <div v-if="isNricRevealed(user._id)">
                                <span class="text-uppercase mr-50">{{ user.nric || '–' }}</span>
                                <b-button
                                  variant="primary"
                                  size="sm"
                                  class="px-50"
                                  @click="hideNric(user._id)"
                                >
                                  Hide full NRIC
                                </b-button>
                              </div>
                              <div v-else>
                                <span class="text-uppercase mr-50">{{ user.encryptedNric || '–' }}</span>
                                <b-button
                                  v-if="canViewThisAction('view-nric', 'NRIC') && user.nric"
                                  variant="primary"
                                  size="sm"
                                  class="px-50"
                                  @click="revealNric(user._id)"
                                >
                                  Show full NRIC
                                </b-button>
                              </div>
                              <span class="text-uppercase">{{ user.relation || '–' }}</span>
                              <br>
                              <span>
                                <b-img
                                  height="22"
                                  :src="require('@/assets/images/admin/birthday.png')"
                                  alt="birthday"
                                  class="mr-25"
                                />
                                {{ user.dob ? dateFormat(user.dob) : '–' }}
                              </span>
                              <br>
                              <b-badge
                                v-if="user.singpass"
                                variant="secondary"
                                class="mt-50 singpass-badge"
                              >
                                via Singpass
                              </b-badge>
                            </b-card-body>
                          </b-card>
                        </b-col>
                      </b-row>
                      <div
                        v-else
                        class="empty_block"
                      >
                        <h4>No Data Yet</h4>
                      </div>
                    </b-tab>
                    <b-tab :title="`Authorised Person (${(salesOrder.authorizedPersons || []).length})`">
                      <b-row
                        v-if="salesOrder.authorizedPersons && salesOrder.authorizedPersons.length"
                        class="match-height"
                      >
                        <b-col
                          v-for="(person, index) in salesOrder.authorizedPersons"
                          :key="person._id"
                          md="6"
                        >
                          <b-card
                            class="card-transaction iu-card"
                            no-body
                          >
                            <b-card-header class="no-bottom-border">
                              <b-card-title>Authorised Person #{{ index + 1 }}</b-card-title>
                              <b-button
                                variant="none"
                                class="btn-icon rounded-circle ml-auto mr-1 flat-primary-bg"
                              >
                                <feather-icon icon="UserIcon" />
                              </b-button>
                              <b-dropdown
                                v-if="canViewThisAction('edit-nominee', 'SalesOrder') || canViewThisAction('nominee-delete', 'SalesOrder')"
                                variant="link"
                                no-caret
                                class="chart-dropdown"
                                toggle-class="p-0"
                                right
                              >
                                <template #button-content>
                                  <feather-icon
                                    icon="MoreVerticalIcon"
                                    size="18"
                                    class="text-body cursor-pointer"
                                  />
                                </template>
                                <b-dropdown-item
                                  v-if="canViewThisAction('edit-nominee', 'SalesOrder')"
                                  @click="openAuthorizedPersonModal(person)"
                                >
                                  <feather-icon icon="EditIcon" />
                                  <span class="align-middle ml-50">Edit</span>
                                </b-dropdown-item>
                                <b-dropdown-item
                                  v-if="canViewThisAction('nominee-delete', 'SalesOrder')"
                                  @click="confirmRemoveAuthorizedPerson(person)"
                                >
                                  <feather-icon icon="Trash2Icon" />
                                  <span class="align-middle ml-50">Remove</span>
                                </b-dropdown-item>
                              </b-dropdown>
                            </b-card-header>
                            <b-card-body>
                              <span class="text-bold-black align-middle text-uppercase">
                                {{ person.name || '–' }}
                              </span>
                              <br>
                              <div v-if="isNricRevealed(person._id)">
                                <span class="text-uppercase mr-50">{{ person.nric || '–' }}</span>
                                <b-button
                                  variant="primary"
                                  size="sm"
                                  class="px-50"
                                  @click="hideNric(person._id)"
                                >
                                  Hide full NRIC
                                </b-button>
                              </div>
                              <div v-else>
                                <span class="text-uppercase mr-50">{{ person.encryptedNric || '–' }}</span>
                                <b-button
                                  v-if="canViewThisAction('view-nric', 'NRIC') && person.nric"
                                  variant="primary"
                                  size="sm"
                                  class="px-50"
                                  @click="revealNric(person._id)"
                                >
                                  Show full NRIC
                                </b-button>
                              </div>
                              <span class="text-uppercase">{{ person.relation || '–' }}</span>
                              <br>
                              <span>
                                <feather-icon
                                  icon="PhoneIcon"
                                  size="18"
                                  class="mr-25 iu-icon"
                                />
                                {{ person.contact || '–' }}
                              </span>
                              <br>
                              <b-badge
                                v-if="person.singpass"
                                variant="secondary"
                                class="mt-50 singpass-badge"
                              >
                                via Singpass
                              </b-badge>
                            </b-card-body>
                          </b-card>
                        </b-col>
                      </b-row>
                      <div
                        v-else
                        class="empty_block"
                      >
                        <h4>No Data Yet</h4>
                      </div>
                    </b-tab>
                  </b-tabs>
                </b-card>
              </b-col>
            </b-row>

            <b-row>
              <b-col cols="12">
                <b-card
                  no-body
                  class="has-padding mt-2 tab-bottom-card"
                >
                  <b-card-body>
                    <b-card-title class="mb-2">
                      Documents
                    </b-card-title>
                    <b-table-simple
                      responsive
                      class="payment-history-table"
                    >
                      <b-thead>
                        <b-tr>
                          <b-th>No.</b-th>
                          <b-th>Document</b-th>
                          <b-th>Action</b-th>
                        </b-tr>
                      </b-thead>
                      <b-tbody>
                        <b-tr
                          v-for="(file, index) in salesOrder.attachments"
                          :key="file._id"
                        >
                          <b-td>{{ index + 1 }}</b-td>
                          <b-td>
                            <div class="d-flex align-items-center justify-content-between">
                              <span>
                                <b-link
                                  v-if="file.fileStorage"
                                  :to="{ name: 'file-view', params: { id: file.fileStorage } }"
                                  target="_blank"
                                >
                                  {{ file.name }}
                                </b-link>
                                <span v-else>{{ file.name }}</span>
                              </span>
                              <span>
                                <b-badge
                                  v-if="file.isForm && !file.needsResign"
                                  variant="light-success"
                                  class="text-capitalize"
                                >
                                  Signed
                                </b-badge>
                                <b-badge
                                  v-else-if="file.needsResign"
                                  variant="light-warning"
                                  class="text-capitalize"
                                >
                                  Pending Re-sign
                                </b-badge>
                                <span
                                  v-if="file.signDate"
                                  class="ml-50"
                                >
                                  On {{ dateFormatWithTime(file.signDate) }}
                                </span>
                              </span>
                            </div>
                          </b-td>
                          <b-td>
                            <b-dropdown
                              variant="link"
                              no-caret
                              right
                            >
                              <template #button-content>
                                <feather-icon
                                  icon="MoreVerticalIcon"
                                  class="text-body"
                                />
                              </template>
                              <b-dropdown-item
                                v-if="file.fileStorage"
                                :to="{ name: 'file-view', params: { id: file.fileStorage } }"
                                target="_blank"
                              >
                                <feather-icon icon="EyeIcon" />
                                <span class="align-middle ml-50">View</span>
                              </b-dropdown-item>
                              <b-dropdown-item
                                v-if="file.isForm && !file.needsResign && canViewThisAction('request-resign', 'SalesOrder')"
                                @click="confirmRequestResign(file)"
                              >
                                <feather-icon icon="RefreshCwIcon" />
                                <span class="align-middle ml-50">Request Re-sign</span>
                              </b-dropdown-item>
                            </b-dropdown>
                          </b-td>
                        </b-tr>
                        <b-tr v-if="!salesOrder.attachments || !salesOrder.attachments.length">
                          <b-td colspan="3">
                            <div class="mt-1">
                              No documents yet
                            </div>
                          </b-td>
                        </b-tr>
                      </b-tbody>
                    </b-table-simple>
                  </b-card-body>
                </b-card>
              </b-col>
            </b-row>
          </b-card>
        </b-tab>

        <template #tabs-end>
          <b-dropdown
            variant="none"
            class="so-tabs-more-btn"
            no-caret
            right
          >
            <template #button-content>
              <feather-icon icon="MoreHorizontalIcon" />
            </template>
            <b-dropdown-item
              v-if="canVoidOrder"
              :disabled="voidingOrder"
              @click="voidSalesOrderAction"
            >
              <feather-icon icon="XCircleIcon" />
              <span class="align-middle ml-50">Void Sales Order</span>
            </b-dropdown-item>
            <b-dropdown-item
              v-else-if="canVoidOrderState"
              v-b-tooltip.hover.left="'You don\'t have the permission for this action'"
              style="opacity: 0.65; pointer-events: auto;"
              aria-disabled="true"
            >
              <feather-icon icon="XCircleIcon" />
              <span class="align-middle ml-50">Void Sales Order</span>
            </b-dropdown-item>
            <b-dropdown-item
              v-else
              disabled
            >
              <span class="align-middle">No actions available</span>
            </b-dropdown-item>
          </b-dropdown>
          <!-- Placeholder for future workflow-step tabs (e.g. SO Approval detail) - visual only. -->
        </template>
      </b-tabs>

      <b-card class="has-padding mt-2">
        <div class="attach__block px-0">
          <b-card-title class="mb-2">
            Attachments
          </b-card-title>
          <b-row v-if="otherAttachments.length">
            <b-col
              v-for="file in otherAttachments"
              :key="file._id"
              class="content-header-left mb-2"
              cols="12"
              md="4"
            >
              <div class="attachment-item">
                <b-img
                  v-if="file.type && file.type.includes('image')"
                  left
                  height="40"
                  :src="require('@/assets/images/admin/doc_jpg.png')"
                  alt="Left image"
                />
                <b-img
                  v-else
                  left
                  height="40"
                  :src="require('@/assets/images/admin/doc_file.png')"
                  alt="Left image"
                />
                <strong>
                  <b-link
                    v-if="file.fileStorage"
                    :to="{ name: 'file-view', params: { id: file.fileStorage } }"
                    target="_blank"
                  >
                    {{ file.name }}
                  </b-link>
                  <span v-else>{{ file.name }}</span>
                </strong>
                <span>{{ file.size ? `${file.size}mb` : '' }}</span>
                <span class="remove__doc">
                  <b-button
                    variant="flat-primary"
                    :disabled="removingAttachmentId === file._id"
                    @click="removeSalesOrderAttachment(file)"
                  >
                    <feather-icon
                      icon="XIcon"
                      class="mr-50"
                      size="18"
                    />
                  </b-button>
                </span>
              </div>
              <div class="doc__desc">
                <span v-if="file.description">
                  {{ file.description }}
                </span>
                <span v-else>
                  <button @click="openAttachmentDescriptionModal(file)">
                    Add a description
                  </button>
                </span>
                <span
                  v-if="file.description"
                  class="edit__desc"
                >
                  <b-button
                    variant="flat-primary"
                    @click="openAttachmentDescriptionModal(file)"
                  >
                    <feather-icon
                      icon="EditIcon"
                      class="mr-50"
                      size="18"
                    />
                  </b-button>
                </span>
              </div>
            </b-col>
          </b-row>
          <div
            v-else
            class="empty_block"
          >
            <h4>Nothing in here yet</h4>
            <b-button
              variant="link"
              @click="triggerAttachmentUpload"
            >
              <feather-icon
                icon="UploadIcon"
                size="20"
                class="mr-50"
              />
              <span
                class="align-middle"
                style="font-weight: 500; font-size: 14px;"
              >
                Upload
              </span>
            </b-button>
            <b-button
              v-if="pendingAttachmentFiles.length"
              variant="success"
              class="mr-1 md-mb-1"
              :disabled="uploadingAttachment"
              @click="saveAttachments"
            >
              Save File ({{ pendingAttachmentFiles.length }})
            </b-button>
          </div>
        </div>
      </b-card>

      <b-modal
        v-model="showAttachmentDescriptionModal"
        size="lg"
        title="Attachment Description"
        modal-class="status-update-modal"
        centered
        no-stacking
        header-class="front-modal"
        @ok="saveAttachmentDescription"
      >
        <b-row>
          <b-col md="12">
            <b-form-textarea
              v-model="attachmentDescriptionValue"
              placeholder="E.g. Please remind to make payment"
            />
          </b-col>
        </b-row>
        <template #modal-footer="{ ok, cancel }">
          <b-button
            variant="flat-primary"
            @click="cancel()"
          >
            Cancel
          </b-button>
          <b-button
            variant="primary"
            :disabled="!attachmentDescriptionValue || savingAttachmentDescription"
            @click="ok()"
          >
            <span class="align-middle">Save</span>
          </b-button>
        </template>
      </b-modal>

      <b-modal
        v-model="showAssignBackModal"
        centered
        size="sm"
        no-close-on-backdrop
        modal-class="status-update-modal iu-modal"
        title="Reassign"
      >
        <b-form-group label="To which department?*">
          <v-select
            v-model="assignBackDepartmentValue"
            :options="assignBackDepartmentOptions"
            label="label"
            :reduce="opt => opt.value"
            :clearable="false"
            placeholder="Select from list"
          />
        </b-form-group>
        <b-form-group label="What's the reason?*">
          <b-form-textarea
            v-model="assignBackNote"
            placeholder="Type to leave a comment"
            rows="4"
          />
        </b-form-group>
        <template #modal-footer="{}">
          <b-button
            variant="flat-primary"
            :disabled="assigningBack"
            @click="showAssignBackModal = false"
          >
            Cancel
          </b-button>
          <b-button
            variant="success-custom"
            :disabled="!assignBackDepartmentValue || !assignBackNote.trim() || assigningBack"
            @click="confirmAssignBack"
          >
            <feather-icon
              icon="UserIcon"
              class="mr-50"
              size="16"
            />
            <span class="align-middle">Reassign</span>
          </b-button>
        </template>
      </b-modal>

      <b-card
        ref="commentsCard"
        class="has-padding mt-2 comments-card"
      >
        <b-card-title class="mb-2 d-flex align-items-center">
          <feather-icon
            icon="MessageSquareIcon"
            size="18"
            class="mr-50"
          />
          Comments
        </b-card-title>
        <app-timeline v-if="salesOrder.comments.length">
          <app-timeline-item
            v-for="(comment, index) in commentsDesc"
            :key="comment._id"
            :variant="getVariant(index)"
            style="border-left: none; padding-bottom: 1rem;"
          >
            <div class="d-flex justify-content-between">
              <strong>{{ comment.author ? comment.author.name : 'Unknown' }}</strong>
              <small class="text-muted">{{ dateFormatWithTime(comment.createdAt) }}</small>
            </div>
            <div>{{ comment.text }}</div>
          </app-timeline-item>
        </app-timeline>
        <div class="d-flex align-items-center mt-1">
          <b-avatar
            variant="light-danger"
            size="32"
            class="mr-1 flex-shrink-0"
            :text="resolveInitials(currentUserName)"
          />
          <b-form-input
            ref="commentInput"
            v-model="newComment"
            placeholder="Add a comment..."
            class="mr-1 comment-input-plain"
            @keyup.enter="submitComment"
          />
          <b-button
            variant="primary"
            class="flex-shrink-0 text-nowrap"
            style="min-width: 165px;"
            :disabled="!newComment.trim() || submittingComment"
            @click="submitComment"
          >
            Add comment
          </b-button>
        </div>
      </b-card>

      <b-card class="has-padding mt-2">
        <b-card-title class="mb-2 d-flex align-items-center">
          <feather-icon
            icon="ListIcon"
            size="18"
            class="mr-50"
          />
          Activity Logs
        </b-card-title>
        <app-timeline v-if="salesOrder.activityLog && salesOrder.activityLog.length">
          <app-timeline-item
            v-for="(log, index) in activityLogDesc"
            :key="index"
            :variant="getVariant(index)"
          >
            <div class="d-flex justify-content-between">
              <strong>{{ log.title || '–' }}</strong>
              <small
                v-if="log.time"
                class="text-muted"
              >{{ dateFormatWithTime(log.time) }}</small>
            </div>
          </app-timeline-item>
        </app-timeline>
        <div
          v-else
          class="text-muted"
        >
          No activity yet
        </div>
      </b-card>
    </div>

    <required-information-edit-modal
      :visible.sync="showRequiredInfoModal"
      :is-little-buddha="salesOrder.productType === 'Little Buddha'"
      :personal-data-consent="salesOrder.personalDataConsent"
      :purchase-declaration="salesOrder.purchaseDeclaration"
      :agent-self-purchase="salesOrder.agentSelfPurchase"
      :marketing-data-consent="salesOrder.marketingDataConsent"
      :names="(salesOrder.endUsers || []).map(user => user.name)"
      :worship-period-start="salesOrder.worshipPeriodStart ? dateFormat(salesOrder.worshipPeriodStart) : null"
      :worship-period-end="salesOrder.worshipPeriodEnd ? dateFormat(salesOrder.worshipPeriodEnd) : null"
      :saving="savingRequiredInfo"
      @save="saveRequiredInformation"
    />

    <service-agent-change-modal
      :visible.sync="showServiceAgentModal"
      :saving="savingServiceAgent"
      :agency-code="salesOrder.serviceAgent ? salesOrder.serviceAgent.agencyCode : null"
      @save="saveServiceAgent"
    />

    <service-agent-change-modal
      :visible.sync="showSalesAgentModal"
      :saving="savingSalesAgent"
      :agency-code="salesOrder.mainAgent ? salesOrder.mainAgent.agencyCode : (salesOrder.agent && salesOrder.agent.agent ? salesOrder.agent.agent.agencyCode : null)"
      @save="saveSalesAgent"
    />

    <purchaser-details-edit-modal
      :visible.sync="showPurchaserDetailsModal"
      :saving="savingPurchaserDetails"
      :singpass="salesOrder.singpass"
      :email-verified="salesOrder.customer ? salesOrder.customer.emailVerified : false"
      :customer-id="salesOrder.customer ? salesOrder.customer._id : null"
      :customer-name="salesOrder.customerName"
      :customer-nric="salesOrder.customerNric"
      :customer-dob="salesOrder.customerDob ? dateFormat(salesOrder.customerDob) : null"
      :customer-sex="salesOrder.customerSex"
      :customer-race="salesOrder.customerRace"
      :customer-nationality="salesOrder.customerNationality"
      :customer-contact="salesOrder.customerContact"
      :customer-email="salesOrder.customerEmail"
      :per-address1="salesOrder.perAddress1"
      :per-address2="salesOrder.perAddress2"
      :per-postcode="salesOrder.perPostcode"
      :per-country="salesOrder.perCountry"
      :cor-address1="salesOrder.corAddress1"
      :cor-address2="salesOrder.corAddress2"
      :cor-postcode="salesOrder.corPostcode"
      :cor-country="salesOrder.corCountry"
      @save="savePurchaserDetails"
    />

    <intended-user-edit-modal
      :visible.sync="showIntendedUserModal"
      :saving="savingIntendedUser"
      :singpass="editingIntendedUser ? editingIntendedUser.singpass : false"
      :name="editingIntendedUser ? editingIntendedUser.name : ''"
      :nric="editingIntendedUser ? editingIntendedUser.nric : ''"
      :dob="editingIntendedUser && editingIntendedUser.dob ? dateFormat(editingIntendedUser.dob) : null"
      :relation="editingIntendedUser ? editingIntendedUser.relation : ''"
      @save="saveIntendedUser"
    />

    <authorized-person-edit-modal
      :visible.sync="showAuthorizedPersonModal"
      :saving="savingAuthorizedPerson"
      :singpass="editingAuthorizedPerson ? editingAuthorizedPerson.singpass : false"
      :name="editingAuthorizedPerson ? editingAuthorizedPerson.name : ''"
      :nric="editingAuthorizedPerson ? editingAuthorizedPerson.nric : ''"
      :contact="editingAuthorizedPerson ? editingAuthorizedPerson.contact : ''"
      :relation="editingAuthorizedPerson ? editingAuthorizedPerson.relation : ''"
      @save="saveAuthorizedPerson"
    />

    <installment-edit-modal
      :visible.sync="showInstallmentModal"
      :saving="savingInstallment"
      :installment-amount="salesOrder.installmentAmount"
      :installment-tenure="salesOrder.installmentTenure"
      :installment-mode="salesOrder.installmentMode"
      :monthly-installment="salesOrder.monthlyInstallment"
      :final-installment="salesOrder.finalInstallment"
      :installment-due-date="salesOrder.installmentDueDate ? dateFormat(salesOrder.installmentDueDate) : null"
      @save="saveInstallment"
    />

    <promotions-edit-modal
      :visible.sync="showPromotionsModal"
      :saving="savingPromotions"
      :promotions="salesOrder.promotions"
      :product-price="salesOrder.productPrice"
      @save="savePromotions"
    />
  </div>
</template>

<script>
import {
  BRow, BCol, BCard, BCardTitle, BCardBody, BCardHeader, BTableSimple, BThead, BTbody, BTr, BTh, BTd, BBadge,
  BTabs, BTab, BAvatar, BButton, BLink, BDropdown, BDropdownItem, BImg, BFormInput, BFormTextarea, BFormGroup, BModal,
  VBTooltip,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import AppTimeline from '@core/components/app-timeline/AppTimeline.vue'
import AppTimelineItem from '@core/components/app-timeline/AppTimelineItem.vue'
import Vue from 'vue'
import { ref, onUnmounted, computed } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import Ripple from 'vue-ripple-directive'
import router from '@/router/index'
import store from '@/store'
import axios from '@axios'
import { useUtils as useAclUtils } from '@/libs/acl/custom'
import { getUserData } from '@/auth/utils'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { resolvePaymentStatusVariant, resolvePaymentStatusLabel } from './paymentStatus'
import salesOrderStoreModule from './salesOrderStoreModule'
import AgentDetailCard from '../submission/AgentDetailCard.vue'
import RequiredInformationEditModal from '../shared/RequiredInformationEditModal.vue'
import ServiceAgentChangeModal from '../shared/ServiceAgentChangeModal.vue'
import PurchaserDetailsEditModal from '../shared/PurchaserDetailsEditModal.vue'
import IntendedUserEditModal from '../shared/IntendedUserEditModal.vue'
import AuthorizedPersonEditModal from '../shared/AuthorizedPersonEditModal.vue'
import InstallmentEditModal from '../shared/InstallmentEditModal.vue'
import PromotionsEditModal from '../shared/PromotionsEditModal.vue'

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BCardTitle,
    BCardBody,
    BTableSimple,
    BThead,
    BTbody,
    BTr,
    BTh,
    BTd,
    BBadge,
    BTabs,
    BTab,
    BAvatar,
    BButton,
    BLink,
    BDropdown,
    BDropdownItem,
    BCardHeader,
    BImg,
    BFormInput,
    BFormTextarea,
    BFormGroup,
    BModal,
    vSelect,
    AgentDetailCard,
    RequiredInformationEditModal,
    ServiceAgentChangeModal,
    PurchaserDetailsEditModal,
    IntendedUserEditModal,
    AuthorizedPersonEditModal,
    InstallmentEditModal,
    PromotionsEditModal,
    AppTimeline,
    AppTimelineItem,
  },
  directives: {
    Ripple,
    'b-tooltip': VBTooltip,
  },
  setup() {
    const SALES_ORDER_APP_STORE_MODULE_NAME = 'app-sales-orders'

    if (!store.hasModule(SALES_ORDER_APP_STORE_MODULE_NAME)) store.registerModule(SALES_ORDER_APP_STORE_MODULE_NAME, salesOrderStoreModule)
    onUnmounted(() => {
      if (store.hasModule(SALES_ORDER_APP_STORE_MODULE_NAME)) store.unregisterModule(SALES_ORDER_APP_STORE_MODULE_NAME)
    })

    const { canViewThisAction } = useAclUtils()
    const toast = useToast()

    const salesOrder = ref(null)
    const newComment = ref('')
    const submittingComment = ref(false)
    const currentUserName = (getUserData() || {}).name || ''
    const resolveInitials = name => (name
      ? name.trim().split(/\s+/).slice(0, 2).map(part => part.charAt(0))
        .join('')
        .toUpperCase()
      : '?')
    // Cycles timeline dot colors by position - same pattern as FileDetail.vue's getVariant, not
    // derived from the log entry's own stored variant (which is fixed per action type and would
    // make every dot the same color for a run of same-type entries).
    const getVariant = key => {
      if (key % 4 === 0) return 'info'
      if (key % 4 === 1) return 'warning'
      if (key % 4 === 2) return 'danger'
      if (key % 4 === 3) return 'purple'
      return 'primary'
    }

    const resolveAcronymName = name => {
      if (name) {
        return name.split(' ').map(item => item.charAt(0)).join('').toUpperCase()
      }

      return '-'
    }

    // Activity Logs card - stored/pushed oldest-first (every backend action appends via
    // [...(salesOrder.activityLog || []), {...}]), but the newest event is what a returning viewer
    // actually wants to see first.
    const activityLogDesc = computed(() => [...(salesOrder.value.activityLog || [])].reverse())
    // Comments card - same oldest-first storage (salesOrder.comments.push in pushNoteAsComment/
    // addComment), same "newest first" preference.
    const commentsDesc = computed(() => [...(salesOrder.value.comments || [])].reverse())

    const revealedNricIds = ref([])
    const isNricRevealed = id => revealedNricIds.value.includes(id)
    const revealNric = id => { if (!revealedNricIds.value.includes(id)) revealedNricIds.value.push(id) }
    const hideNric = id => {
      const index = revealedNricIds.value.indexOf(id)
      if (index > -1) revealedNricIds.value.splice(index, 1)
    }

    store.dispatch('app-sales-orders/fetchSalesOrder', router.currentRoute.params.id)
      .then(response => {
        salesOrder.value = response.data

        const bcLength = store.state.breadcrumb.breadcrumbs.length
        if (bcLength > 1) {
          store.commit('breadcrumb/REMOVE_BREADCRUMB')
        }
        store.commit('breadcrumb/UPDATE_BREADCRUMB', {
          active: true,
          text: `${salesOrder.value.stringID} - ${salesOrder.value.customerName}`,
        })
      })

    // Enhancement #3 - per-product tenure/downpayment thresholds (ReservationSetting.
    // productTypeTenureSettings, Enhancement #2), so the Downpayment/Tenure rows below can flag
    // which threshold (if any) this order violates. No existing fetch/cache for this anywhere in
    // the frontend - fetched fresh here, same endpoint the agency Settings page itself uses.
    const reservationSetting = ref(null)
    axios.get('reservation/reservation-settings')
      .then(response => { reservationSetting.value = response.data })
      .catch(() => {})

    // Same bracket/tax-style tier match as the backend's findTenureTier
    // (helpers/sales-order-workflow.js) - tiers sorted ascending by thresholdAmount, last one
    // whose thresholdAmount <= productPrice wins. Purely a display concern (the real gate lives
    // server-side in the step engine), small acceptable duplication rather than a shared module.
    const matchedTenureTier = computed(() => {
      if (!salesOrder.value || !reservationSetting.value) return null
      const settings = reservationSetting.value.productTypeTenureSettings || []
      const entry = settings.find(e => e.productType === salesOrder.value.productType)
      const tiers = entry ? entry.tiers : []
      if (!tiers.length) return null
      const sorted = [...tiers].sort((a, b) => (Number(a.thresholdAmount) || 0) - (Number(b.thresholdAmount) || 0))
      let matched = sorted[0]
      const purchaseAmount = Number(salesOrder.value.productPrice) || 0
      sorted.forEach(tier => {
        if ((Number(tier.thresholdAmount) || 0) <= purchaseAmount) matched = tier
      })
      return matched
    })

    const isDownpaymentBelowMinimum = computed(() => !!(matchedTenureTier.value
      && matchedTenureTier.value.minDownPaymentPercentage != null
      && salesOrder.value
      && (Number(salesOrder.value.downPaymentPercentage) || 0) < matchedTenureTier.value.minDownPaymentPercentage))

    const isTenureAboveMaximum = computed(() => !!(matchedTenureTier.value
      && matchedTenureTier.value.maxTenureMonths != null
      && salesOrder.value
      && (Number(salesOrder.value.installmentTenure) || 0) > matchedTenureTier.value.maxTenureMonths))

    const pluralizedProductType = computed(() => (salesOrder.value && salesOrder.value.productType
      ? `${salesOrder.value.productType.toLowerCase()}s`
      : ''))

    const addressLine = (line1, line2, postcode, country) => [line1, line2, postcode, country].filter(Boolean).join(', ')
    const permanentAddress = computed(() => (salesOrder.value
      ? addressLine(salesOrder.value.perAddress1, salesOrder.value.perAddress2, salesOrder.value.perPostcode, salesOrder.value.perCountry)
      : ''))
    const correspondenceAddress = computed(() => (salesOrder.value
      ? addressLine(salesOrder.value.corAddress1, salesOrder.value.corAddress2, salesOrder.value.corPostcode, salesOrder.value.corCountry)
      : ''))

    // This order's own submitting agent - mainAgent (a confirmed Agent) or agent (still a
    // ProbationaryAgent), mutually exclusive. Drives which AgentDetailCard field layout to use.
    const salesAgentDetail = computed(() => (salesOrder.value ? (salesOrder.value.mainAgent || salesOrder.value.agent) : null))
    const salesAgentType = computed(() => (salesOrder.value && salesOrder.value.mainAgent ? 'Agent' : 'ProbationaryAgent'))

    // Mirrors computeOrderTotal exactly (controllers/sales/SalesOrderController.js /
    // helpers/sales-order.js) - discounts[] is a separate field from promotions[], both must be
    // subtracted. discounts[] is never actually populated by anything in the current codebase, so
    // this was a silent no-op today, but would have silently underestimated the total the moment
    // that field gets wired up (e.g. a future manual-discount feature reusing it).
    const discountsTotal = order => [...(order.discounts || []), ...(order.promotions || [])]
      .reduce((sum, d) => sum + (Number(d.amount) || 0), 0)
    const orderSubtotal = computed(() => {
      if (!salesOrder.value) return 0
      return (Number(salesOrder.value.productPrice) || 0) + (Number(salesOrder.value.maintenanceFee) || 0) - discountsTotal(salesOrder.value)
    })
    const orderTotal = computed(() => orderSubtotal.value + (Number(salesOrder.value.gstAmount) || 0))

    // Computed here, reactively, instead of trusting salesOrder.paymentStatus off the server - that
    // field is only ever attached by the initial fetchSalesOrder load (SalesOrderController.js's
    // findSalesOrder), never by any of the other action responses this page Object.assign()s in
    // afterward. updatePromotions in particular changes orderTotal (via promotions[]) without
    // returning a recomputed paymentStatus, which left this badge showing a stale Paid/Partially
    // Paid/Overpaid label until a full page reload. Mirrors resolvePaymentStatus's exact formula
    // (controllers/sales/SalesOrderController.js) off orderTotal/amountPaid, both of which DO stay
    // correctly in sync via the normal merge.
    const paymentStatus = computed(() => {
      if (!salesOrder.value) return null
      const amountPaid = Number(salesOrder.value.amountPaid) || 0
      if (amountPaid <= 0) return 'Pending'
      if (amountPaid > orderTotal.value) return 'Overpaid'
      if (amountPaid < orderTotal.value) return 'Partially Paid'
      return 'Paid'
    })

    // "Documents" table above already lists the digital forms (isForm) - this card is for
    // whatever else got attached (isForm false), matching the mockup's separate treatment.
    const otherAttachments = computed(() => (salesOrder.value ? (salesOrder.value.attachments || []).filter(file => !file.isForm) : []))

    const purchaseOptionLabel = paymentType => {
      if (paymentType === 'downpayment') return 'Pay Downpayment'
      if (paymentType === 'bookingfee') return 'Pay Booking Fee'
      return 'Full Payment'
    }

    const needTypeLabel = needType => {
      if (needType === 'PRE-NEED') return 'Pre-need'
      if (needType === 'AS-NEED') return 'As-need'
      return needType || '–'
    }

    // 5 visual stages the client's 8 granular steps (see helpers/sales-order-workflow.js on the
    // backend, kept in sync here) group into - STAGE_FOR_STEP maps a step number to its stage.
    const workflowSteps = [
      { key: 'probationary-agent', label: 'Probationary Agent' },
      { key: 'special-requests', label: 'Special Requests' },
      { key: 'sales-order', label: 'Sales Order' },
      { key: 'installments', label: 'Installments' },
      { key: 'so-approval', label: 'SO Approval' },
    ]
    const STAGE_FOR_STEP = {
      1: 0, 2: 0, 3: 1, 4: 1, 5: 2, 6: 3, 7: 3, 8: 4,
    }
    // Inverse of STAGE_FOR_STEP - which granular step number(s) a stage's icon row shows, in order.
    const STEPS_FOR_STAGE = {
      0: [1, 2], 1: [3, 4], 2: [5], 3: [6, 7], 4: [8],
    }
    const STEP_LABELS = {
      1: 'Verify Probationary Agent',
      2: 'Approve Probationary Agent',
      3: 'Review Special Discounts',
      4: 'Review Special Instalment Tenure',
      5: 'Review Information & Documents',
      6: 'Calculate Monthly Instalments',
      7: 'Setup Re-payment Arrangements',
      8: 'Approve SO',
    }
    // Past-tense label shown once a step is done (the completed-icon tooltip's bold line) -
    // distinct copy from STEP_LABELS, not just "done" appended. Confirmed against Figma for step
    // 1 ("Probationary Agent Verified"); steps 2-8 inferred by the same pattern pending their own
    // screens - flag/replace if the real copy differs once shared.
    const STEP_COMPLETED_LABELS = {
      1: 'Probationary Agent Verified',
      2: 'Probationary Agent Approved',
      3: 'Special Discounts Reviewed',
      4: 'Special Tenure Reviewed',
      5: 'Information and Documents Reviewed',
      6: 'Installment Arrangements Processed',
      7: 'Re-payment Arrangements Set Up',
      8: 'SO Approved',
    }
    // Every step in a stage always gets an icon, applicable or not - an inapplicable step (e.g.
    // steps 1/2 on a non-probationary order) shows disabled/grey rather than disappearing.
    const stepsForStage = stageIndex => STEPS_FOR_STAGE[stageIndex] || []
    // A step is done once it's behind currentStep in the order's own applicableSteps sequence -
    // structural position, not stepHistory presence, so it stays consistent even if stepHistory is
    // sparse (e.g. an order created before this feature shipped).
    const isStepCompleted = step => {
      if (!salesOrder.value) return false
      if (salesOrder.value.workflowStatus === 'Approved') return true
      const steps = salesOrder.value.applicableSteps || []
      const stepIdx = steps.indexOf(step)
      const currentIdx = steps.indexOf(salesOrder.value.currentStep)
      return stepIdx !== -1 && currentIdx !== -1 && stepIdx < currentIdx
    }
    const stepIconStatus = step => {
      if (!salesOrder.value || !(salesOrder.value.applicableSteps || []).includes(step)) return 'disabled'
      if (isStepCompleted(step)) return 'completed'
      if (salesOrder.value.currentStep === step) return 'current'
      return 'pending'
    }
    // Whole stage card greys out only once every one of its steps is inapplicable - a stage with
    // a mix (e.g. one disabled, one pending/current) stays in its normal look.
    const isStageDisabled = stageIndex => stepsForStage(stageIndex).length > 0
      && stepsForStage(stageIndex).every(step => stepIconStatus(step) === 'disabled')
    // Most recent 'Completed' stepHistory entry for this step number - a step can in principle be
    // completed more than once if it was later Reverted past and re-completed.
    const stepCompletedByName = step => {
      if (!salesOrder.value) return null
      const entries = (salesOrder.value.stepHistory || []).filter(entry => entry.action === 'Completed' && entry.step === step)
      if (!entries.length) return null
      const latest = entries[entries.length - 1]
      return latest.actionBy ? latest.actionBy.name : null
    }
    const stepIconTooltip = step => {
      if (stepIconStatus(step) === 'completed') {
        const name = stepCompletedByName(step)
        const title = STEP_COMPLETED_LABELS[step] || STEP_LABELS[step]
        return name ? `<strong>${title}</strong><br><small>By ${name}</small>` : `<strong>${title}</strong>`
      }
      const dept = salesOrder.value && salesOrder.value.stepDepartments ? salesOrder.value.stepDepartments[step] : null
      const title = STEP_LABELS[step]
      return dept ? `<strong>${title}</strong><br><small>${dept}</small>` : `<strong>${title}</strong>`
    }
    // -1 (nothing active/done) before the workflow starts or once fully Approved every stage
    // shows done (index past the last stage).
    const currentStepIndex = computed(() => {
      if (!salesOrder.value) return -1
      if (salesOrder.value.workflowStatus === 'Approved') return workflowSteps.length
      if (!salesOrder.value.currentStep) return -1
      return STAGE_FOR_STEP[salesOrder.value.currentStep] !== undefined ? STAGE_FOR_STEP[salesOrder.value.currentStep] : -1
    })
    const isWorkflowActive = computed(() => !!(salesOrder.value
      && salesOrder.value.currentStep
      && salesOrder.value.workflowStatus !== 'Approved'
      && salesOrder.value.status !== 'Voided'))
    // isReviewingDept/isStepOwnerDept/isAssignedDept come straight off the backend (findSalesOrder
    // / every workflow response) - it already knows req.user, no point re-deriving department
    // ownership client-side. isReviewingDept is the STRICT "can actually act" check (natural owner
    // AND no active Assign-back override - an override blocks completion for everyone, including
    // the natural owner, until it's cleared). isStepOwnerDept ignores the override, so the natural
    // owner still sees "Complete this step" (disabled, with a tooltip) rather than it disappearing.
    const isReviewingDept = computed(() => isWorkflowActive.value && !!(salesOrder.value && salesOrder.value.isReviewingDept))
    const isStepOwnerDept = computed(() => isWorkflowActive.value && !!(salesOrder.value && salesOrder.value.isStepOwnerDept))
    // Gates Send back / Re-assign back to owner - the step's owner, always; the department it's
    // currently been assigned to, only while that Assign-back override is active. Re-assign (to a
    // different department) uses isStepOwnerDept directly instead (owner-only, always).
    const canActOnUnownedStep = computed(() => isWorkflowActive.value && !!(salesOrder.value && salesOrder.value.canActOnUnownedStep))
    // Gates "Re-assign back to {owner}" alone - the currently-assigned department only, never the
    // owner (re-assigning a step back to itself is a no-op this button isn't for).
    const canReassignBackToOwner = computed(() => isWorkflowActive.value && !!(salesOrder.value && salesOrder.value.canReassignBackToOwner))
    const canActOnStep = computed(() => isWorkflowActive.value
      && !!(salesOrder.value && (salesOrder.value.isReviewingDept || salesOrder.value.isAssignedDept)))
    // Split from the permission check below so the Void item can still render (disabled, with a
    // permission tooltip) for a reviewing-dept user who lacks the 'void' permission, instead of
    // disappearing entirely - matches the Edit-button pattern elsewhere (ServiceFormDetail.vue).
    const canVoidOrderState = computed(() => !!(salesOrder.value
      && salesOrder.value.status !== 'Voided'
      && salesOrder.value.workflowStatus !== 'Approved'
      && (!salesOrder.value.currentStep || salesOrder.value.isReviewingDept)))
    const canVoidOrder = computed(() => canVoidOrderState.value && canViewThisAction('void', 'SalesOrder'))
    // status/workflowStatus are the two real fields - status only ever flips to 'Voided', so
    // everything else is read off workflowStatus instead. Mirrors the exact same priority order
    // the backend already computes for the list/count pages (statusAddFieldsStages,
    // controllers/sales/SalesOrderController.js) and the app (resolveAppOrderStatus,
    // controllers/app/SalesController.js) - this page was missing the "Action Required" state
    // entirely, so a sent-back order (workflowStatus stays 'In Progress' - send-back only resets
    // currentStep, not workflowStatus) showed "Processing" here while the list correctly showed
    // "Action Required" for the very same order.
    const workflowStatusLabel = computed(() => {
      if (!salesOrder.value) return '–'
      if (salesOrder.value.status === 'Voided') return 'Voided'
      if (salesOrder.value.workflowStatus === 'Approved') return 'Processed'
      const stepHistory = salesOrder.value.stepHistory || []
      const lastAction = stepHistory.length ? stepHistory[stepHistory.length - 1].action : null
      if (lastAction === 'Sent Back to Agent') return 'Action Required'
      if (salesOrder.value.workflowStatus === 'In Progress') return 'Processing'
      return '–'
    })
    // Nothing new for the reviewing department to act on until the agent has actually resubmitted
    // (controllers/app/SalesController.js's editSalesOrder pushes a 'Resubmitted by Agent' entry
    // the moment they do, clearing this) - Send back / Complete this step remain hidden the whole
    // time an order sits in Action Required, since there's no new information to Complete against
    // and re-sending back would just repeat the same still-unresolved request.
    const isActionRequired = computed(() => workflowStatusLabel.value === 'Action Required')
    const verificationMessage = computed(() => {
      if (!isWorkflowActive.value) return null
      const stepLabel = STEP_LABELS[salesOrder.value.currentStep]
      // Always the step's owning department, never wherever it's been Assign-back'd to (that's
      // what the "Pending Dept." badge - salesOrder.pendingDepartment - shows instead).
      const dept = salesOrder.value.stepOwnerDepartment
      return dept && stepLabel ? `${dept} needs to ${stepLabel.toLowerCase()}` : null
    })
    const workflowActionError = error => {
      toast({
        component: ToastificationContent,
        props: {
          title: (error.data && error.data.message) || process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
          icon: 'AlertTriangleIcon',
          variant: 'danger',
        },
      })
    }

    const attachmentInput = ref(null)
    const uploadingAttachment = ref(false)
    const pendingAttachmentFiles = ref([])
    const triggerAttachmentUpload = () => {
      attachmentInput.value.click()
    }
    const onAttachmentFileSelected = event => {
      pendingAttachmentFiles.value.push(...event.target.files)
      // eslint-disable-next-line no-param-reassign
      event.target.value = null
    }
    // Files picked via the toolbar's Upload button are staged, not sent yet - Save File uploads
    // them all in one go, matching PurchaseRequestDetail's two-step upload/save flow.
    const saveAttachments = async () => {
      uploadingAttachment.value = true
      const filesToUpload = pendingAttachmentFiles.value
      pendingAttachmentFiles.value = []

      try {
        // eslint-disable-next-line no-restricted-syntax
        for (const file of filesToUpload) {
          // eslint-disable-next-line no-await-in-loop
          const response = await store.dispatch('app-sales-orders/addAttachment', { id: salesOrder.value._id, file })
          salesOrder.value.attachments = response.data.attachments
        }
        toast({
          component: ToastificationContent,
          props: {
            title: filesToUpload.length > 1 ? 'Files uploaded' : 'File uploaded',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        workflowActionError(error)
      } finally {
        uploadingAttachment.value = false
      }
    }

    const removingAttachmentId = ref(null)
    const removeSalesOrderAttachment = file => {
      removingAttachmentId.value = file._id
      store.dispatch('app-sales-orders/removeAttachment', { id: salesOrder.value._id, attachmentId: file._id })
        .then(response => {
          salesOrder.value.attachments = response.data.attachments
        })
        .catch(workflowActionError)
        .finally(() => {
          removingAttachmentId.value = null
        })
    }

    const editingAttachmentId = ref(null)
    const attachmentDescriptionValue = ref('')
    const savingAttachmentDescription = ref(false)
    const showAttachmentDescriptionModal = ref(false)
    const openAttachmentDescriptionModal = file => {
      editingAttachmentId.value = file._id
      attachmentDescriptionValue.value = file.description || ''
      showAttachmentDescriptionModal.value = true
    }
    const saveAttachmentDescription = () => {
      savingAttachmentDescription.value = true
      store.dispatch('app-sales-orders/updateAttachmentDescription', {
        id: salesOrder.value._id,
        attachmentId: editingAttachmentId.value,
        description: attachmentDescriptionValue.value,
      })
        .then(response => {
          salesOrder.value.attachments = response.data.attachments
          showAttachmentDescriptionModal.value = false
        })
        .catch(workflowActionError)
        .finally(() => {
          savingAttachmentDescription.value = false
        })
    }

    const commentInput = ref(null)
    const commentsCard = ref(null)
    const focusCommentBox = () => {
      const cardEl = commentsCard.value ? commentsCard.value.$el : null
      if (cardEl) cardEl.scrollIntoView({ behavior: 'smooth', block: 'center' })
      const inputEl = commentInput.value ? commentInput.value.$el : null
      if (inputEl) inputEl.focus()
    }

    // Body paragraph, ending in the same "Are you sure you want to complete this step?" line each
    // time. The department named mid-sentence is whichever department the NEXT step hands off to
    // (confirmed via step 4's copy naming CS - step 5's owner, not step 4's own BD) - except step
    // 5/6, where the next step is the same CS department, so it says "will proceed" rather than
    // "informed". Steps 1/2 name the probationary agent specifically (confirmed via step 2's own
    // screen), so those two entries are functions taking the live salesOrder rather than plain
    // strings. Confirmed for steps 1, 2, 3, 4, 5, 6; step 7 modeled on step 6 pending its own
    // screen (step 8 is a special case, handled separately below).
    const STEP_COMPLETE_BODY = {
      1: order => `Once you verify this probationary agent ${order.agentName}, BD will proceed to the next step. Are you sure you want to complete this step?`,
      2: order => `Once you approve this probationary agent ${order.agentName}, BD will proceed to the next step. Are you sure you want to complete this step?`,
      3: 'After reviewing the Special Discount, BD will proceed to the next step. Are you sure you want to complete this step?',
      4: 'After reviewing the Special Tenure, CS will be informed to the next step. Are you sure you want to complete this step?',
      5: 'After reviewing the information and documents, CS will proceed to the next step. Are you sure you want to complete this step?',
      6: 'After processing the installment, CS will proceed to the next step. Are you sure you want to complete this step?',
      // No real screen for step 7 yet - modeled on step 6's confirmed copy/pattern (same CS
      // department continuing on to the next step) per explicit instruction, not its own design.
      7: 'After setting up the re-payment arrangements, CS will proceed to the next step. Are you sure you want to complete this step?',
    }

    const completingStep = ref(false)
    const completeStep = () => {
      const step = salesOrder.value.currentStep
      // Step 8 (final - Approve SO) has its own dialog copy/wording, not the generic "Are you
      // sure you want to complete this step?" pattern every other step uses - it's also where the
      // Sales Order actually flips to Approved, so it's framed as final/irreversible. SCLC sync
      // isn't called here yet (see confirmLotForOrder's own comment in the backend) - a future
      // follow-up once that API exists, not this dialog's concern.
      const isFinalStep = step === 8
      const title = isFinalStep ? 'Approve Sales Order (SO)' : 'Are you sure you want to complete this step?'
      const bodyEntry = STEP_COMPLETE_BODY[step]
      const genericBody = 'Once you complete this step, the Sales Order will proceed to the next step. Are you sure you want to complete this step?'
      const body = isFinalStep
        ? 'You are about to approve this Sales Order. This action is final and cannot be undone.'
        : (typeof bodyEntry === 'function' ? bodyEntry(salesOrder.value) : bodyEntry) || genericBody
      const inputPlaceholder = isFinalStep
        ? '(Optional) Leave a comment'
        : '(Optional) Leave a comment. E.g. Any notes or information you want to include in this Sales Order as you complete this step.'
      const confirmButtonText = isFinalStep ? 'Yes, approve' : 'Yes, complete'

      Vue.swal({
        title,
        html: body,
        // eslint-disable-next-line global-require
        imageUrl: require('@/assets/images/icons/success.png'),
        imageWidth: 80,
        imageHeight: 80,
        input: 'textarea',
        inputPlaceholder,
        reverseButtons: true,
        showCancelButton: true,
        cancelButtonText: 'No, go back',
        confirmButtonText,
        customClass: { confirmButton: 'btn btn-success ml-1 mt-2', cancelButton: 'btn btn-outline-primary mt-2' },
        buttonsStyling: false,
      }).then(result => {
        // Comment is optional - an empty textarea still confirms, so check isConfirmed rather
        // than the (possibly empty-string, falsy) result.value like the required-input swals do.
        if (!result.isConfirmed) return
        completingStep.value = true
        store.dispatch('app-sales-orders/completeWorkflowStep', { id: salesOrder.value._id, note: result.value })
          .then(response => {
            Object.assign(salesOrder.value, response.data)
          })
          .catch(workflowActionError)
          .finally(() => {
            completingStep.value = false
          })
      })
    }

    const sendingBack = ref(false)
    const sendBackToSalesAgent = () => {
      Vue.swal({
        title: 'Send this Sales Order back to agent?',
        html: 'Please let the agent knows why you want to send this Sales Order back.',
        // eslint-disable-next-line global-require
        imageUrl: require('@/assets/images/icons/fly.png'),
        imageWidth: 80,
        imageHeight: 80,
        imageAlt: 'Custom Icon',
        input: 'textarea',
        inputPlaceholder: 'Leave a comment',
        showCancelButton: true,
        reverseButtons: true,
        cancelButtonText: 'Cancel',
        confirmButtonText: 'Send back',
        customClass: {
          popup: 'send-back-swal',
          confirmButton: 'btn btn-success ml-1 mt-2',
          cancelButton: 'btn btn-outline-primary mt-2',
        },
        buttonsStyling: false,
        inputValidator: value => (!value || !value.trim() ? 'A note is required' : undefined),
      }).then(result => {
        if (!result.value) return
        sendingBack.value = true
        store.dispatch('app-sales-orders/sendBackToAgent', { id: salesOrder.value._id, note: result.value })
          .then(response => {
            Object.assign(salesOrder.value, response.data)
            toast({
              component: ToastificationContent,
              props: {
                title: 'Sent back to agent',
                icon: 'CheckIcon',
                variant: 'success',
              },
            })
          })
          .catch(workflowActionError)
          .finally(() => {
            sendingBack.value = false
          })
      })
    }

    // Earlier applicable steps than the current one - the only valid Revert targets.
    const earlierApplicableSteps = computed(() => {
      if (!salesOrder.value) return []
      const steps = salesOrder.value.applicableSteps || []
      const currentIndex = steps.indexOf(salesOrder.value.currentStep)
      return currentIndex <= 0 ? [] : steps.slice(0, currentIndex)
    })
    // No step picker - Figma confirms Revert always targets the single immediately-previous
    // applicable step (earlierApplicableSteps' last entry), not an arbitrary earlier one.
    const revertingStep = ref(false)
    const revertStep = () => {
      const toStep = earlierApplicableSteps.value[earlierApplicableSteps.value.length - 1]
      if (toStep === undefined) return

      Vue.swal({
        title: 'Are you sure you want to revert back to previous step?',
        html: 'Once you agree to revert, the relevant department(s) will be notified, and the step will need to be redone. Are you sure you want to proceed?',
        // eslint-disable-next-line global-require
        imageUrl: require('@/assets/images/icons/rotate-red.png'),
        imageWidth: 80,
        imageHeight: 80,
        input: 'textarea',
        inputPlaceholder: 'Leave a comment',
        inputValidator: value => (!value || !value.trim() ? 'A reason is required' : undefined),
        reverseButtons: true,
        showCancelButton: true,
        cancelButtonText: 'No, go back',
        confirmButtonText: 'Yes, revert',
        customClass: { confirmButton: 'btn btn-danger ml-1 mt-2', cancelButton: 'btn btn-outline-primary mt-2' },
        buttonsStyling: false,
      }).then(result => {
        if (!result.value) return
        revertingStep.value = true
        store.dispatch('app-sales-orders/revertToStep', { id: salesOrder.value._id, toStep, note: result.value })
          .then(response => {
            Object.assign(salesOrder.value, response.data)
          })
          .catch(workflowActionError)
          .finally(() => {
            revertingStep.value = false
          })
      })
    }

    const assigningBack = ref(false)
    const showAssignBackModal = ref(false)
    const assignBackDepartmentOptions = ref([])
    const assignBackDepartmentValue = ref(null)
    const assignBackNote = ref('')
    const assignBackToDept = () => {
      axios.get('directory/departments/respond-with/name-and-id')
        .then(response => {
          // Re-assigning to the step's own owning department is a no-op the backend now rejects
          // outright - excluded here too so the picker never offers a choice that can't go
          // anywhere.
          assignBackDepartmentOptions.value = response.data.departments
            .filter(department => department.name !== salesOrder.value.stepOwnerDepartment)
            .map(department => ({ label: department.name, value: department._id }))
          assignBackDepartmentValue.value = null
          assignBackNote.value = ''
          showAssignBackModal.value = true
        })
        .catch(workflowActionError)
    }
    const confirmAssignBack = () => {
      if (!assignBackDepartmentValue.value || !assignBackNote.value.trim()) return
      assigningBack.value = true
      store.dispatch('app-sales-orders/assignBackToDepartment', {
        id: salesOrder.value._id,
        toDepartment: assignBackDepartmentValue.value,
        note: assignBackNote.value.trim(),
      })
        .then(response => {
          Object.assign(salesOrder.value, response.data)
          showAssignBackModal.value = false
        })
        .catch(workflowActionError)
        .finally(() => {
          assigningBack.value = false
        })
    }

    // Undoes an Assign-back override, handing the step back to its natural department - the only
    // way anyone regains the ability to Complete it. Open to any department, no note needed (it's
    // reversing a handoff, not making a new decision), so this fires directly rather than opening
    // a confirm dialog like the other workflow actions.
    const reassigningBackToOwner = ref(false)
    const reassignBackToOwner = () => {
      reassigningBackToOwner.value = true
      store.dispatch('app-sales-orders/reassignBackToOwner', { id: salesOrder.value._id })
        .then(response => {
          Object.assign(salesOrder.value, response.data)
          toast({
            component: ToastificationContent,
            props: {
              title: 'Re-assigned back to owning department',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
        })
        .catch(workflowActionError)
        .finally(() => {
          reassigningBackToOwner.value = false
        })
    }

    const voidingOrder = ref(false)
    const voidSalesOrderAction = () => {
      Vue.swal({
        title: `Void Sales Order ${salesOrder.value.stringID}?`,
        html: 'Once you void the Sales Order, this action is irreversible. No notification is sent out to agent or customer.',
        // eslint-disable-next-line global-require
        imageUrl: require('@/assets/images/icons/alert.png'),
        imageWidth: 80,
        imageHeight: 80,
        reverseButtons: true,
        showCancelButton: true,
        cancelButtonText: 'No, go back',
        confirmButtonText: 'Yes, void it',
        customClass: { confirmButton: 'btn btn-danger ml-1 mt-2', cancelButton: 'btn btn-outline-primary mt-2' },
        buttonsStyling: false,
      }).then(result => {
        if (!result.value) return
        const orderNo = salesOrder.value.stringID
        voidingOrder.value = true
        store.dispatch('app-sales-orders/voidSalesOrder', { id: salesOrder.value._id })
          .then(response => {
            Object.assign(salesOrder.value, response.data)
            toast({
              component: ToastificationContent,
              props: {
                title: 'Voided Successfully',
                text: `Sales Order ${orderNo} has been voided.`,
                icon: 'CheckCircleIcon',
                variant: 'success',
              },
            })
          })
          .catch(workflowActionError)
          .finally(() => {
            voidingOrder.value = false
          })
      })
    }

    const requestingResign = ref(false)
    const confirmRequestResign = file => {
      Vue.swal({
        title: 'Request Re-sign?',
        html: `You can request customer to re-sign the document <strong>${file.name}</strong>. When re-signing, the latest data from the Sales Order will be reflected.<br><br><strong>Remember to Send back to Sales Agent so they are able to access the signing functions.</strong>`,
        // eslint-disable-next-line global-require
        imageUrl: require('@/assets/images/icons/warning.png'),
        imageWidth: 80,
        imageHeight: 80,
        reverseButtons: true,
        showCancelButton: true,
        cancelButtonText: 'No, cancel',
        confirmButtonText: 'Yes, request re-sign',
        customClass: { confirmButton: 'btn btn-primary ml-1 mt-2', cancelButton: 'btn btn-outline-primary mt-2' },
        buttonsStyling: false,
      }).then(result => {
        if (!result.value) return
        requestingResign.value = true
        store.dispatch('app-sales-orders/requestResign', { id: salesOrder.value._id, attachmentId: file._id })
          .then(response => {
            Object.assign(salesOrder.value, response.data)
            toast({
              component: ToastificationContent,
              props: {
                title: 'Re-sign Requested',
                text: `The agent has been notified to re-sign "${file.name}".`,
                icon: 'CheckCircleIcon',
                variant: 'success',
              },
            })
          })
          .catch(workflowActionError)
          .finally(() => {
            requestingResign.value = false
          })
      })
    }

    const confirmRemoveIntendedUser = user => {
      Vue.swal({
        title: 'Remove this Intended User?',
        html: `<strong>${user.name || ''}</strong> will be removed from this Sales Order.`,
        reverseButtons: true,
        showCancelButton: true,
        cancelButtonText: 'Cancel',
        confirmButtonText: 'Yes, Remove',
        customClass: { confirmButton: 'btn btn-danger ml-1 mt-2', cancelButton: 'btn btn-outline-primary mt-2' },
        buttonsStyling: false,
      }).then(result => {
        if (!result.value) return
        store.dispatch('app-sales-orders/removeIntendedUser', { id: salesOrder.value._id, userId: user._id })
          .then(response => {
            Object.assign(salesOrder.value, response.data)
          })
          .catch(workflowActionError)
      })
    }

    const confirmRemoveAuthorizedPerson = person => {
      Vue.swal({
        title: 'Remove this Authorised Person?',
        html: `<strong>${person.name || ''}</strong> will be removed from this Sales Order.`,
        reverseButtons: true,
        showCancelButton: true,
        cancelButtonText: 'Cancel',
        confirmButtonText: 'Yes, Remove',
        customClass: { confirmButton: 'btn btn-danger ml-1 mt-2', cancelButton: 'btn btn-outline-primary mt-2' },
        buttonsStyling: false,
      }).then(result => {
        if (!result.value) return
        store.dispatch('app-sales-orders/removeAuthorizedPerson', { id: salesOrder.value._id, userId: person._id })
          .then(response => {
            Object.assign(salesOrder.value, response.data)
          })
          .catch(workflowActionError)
      })
    }

    const submitComment = () => {
      if (!newComment.value.trim()) return
      submittingComment.value = true
      store.dispatch('app-sales-orders/addComment', { id: salesOrder.value._id, text: newComment.value.trim() })
        .then(response => {
          salesOrder.value.comments = response.data.comments
          newComment.value = ''
        })
        .catch(error => {
          toast({
            component: ToastificationContent,
            props: {
              title: (error.data && error.data.message) || process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
        .finally(() => {
          submittingComment.value = false
        })
    }

    const showRequiredInfoModal = ref(false)
    const savingRequiredInfo = ref(false)

    const saveRequiredInformation = formData => {
      savingRequiredInfo.value = true
      store.dispatch('app-sales-orders/updateRequiredInformation', { id: salesOrder.value._id, formData })
        .then(response => {
          Object.assign(salesOrder.value, response.data)
          showRequiredInfoModal.value = false
          Vue.swal({
            title: 'Required Information Updated',
            html: '',
            // eslint-disable-next-line global-require
            imageUrl: require('@/assets/images/icons/success.png'),
            imageWidth: 80,
            imageHeight: 80,
            imageAlt: 'Custom Icon',
            showCancelButton: false,
            confirmButtonText: 'Okay',
            customClass: {
              confirmButton: 'btn btn-primary mt-2',
            },
            buttonsStyling: false,
          })
        })
        .catch(error => {
          toast({
            component: ToastificationContent,
            props: {
              title: (error.data && error.data.message) || process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
        .finally(() => {
          savingRequiredInfo.value = false
        })
    }

    // Unlike Submission, this is a frozen snapshot - saving here only touches this one order's
    // own serviceAgent fields, never the originating submission or any sibling order.
    const showServiceAgentModal = ref(false)
    const savingServiceAgent = ref(false)

    const saveServiceAgent = ({ agentId }) => {
      savingServiceAgent.value = true
      store.dispatch('app-sales-orders/updateServiceAgent', { id: salesOrder.value._id, agentId })
        .then(response => {
          Object.assign(salesOrder.value, response.data)
          showServiceAgentModal.value = false
          Vue.swal({
            title: 'Agent Details Updated',
            html: "Please send this back to the newly assigned agent for further action by clicking the 'Send back to Sales Agent' button.",
            // eslint-disable-next-line global-require
            imageUrl: require('@/assets/images/icons/success.png'),
            imageWidth: 80,
            imageHeight: 80,
            imageAlt: 'Custom Icon',
            showCancelButton: false,
            confirmButtonText: 'Okay',
            customClass: {
              confirmButton: 'btn btn-primary mt-2',
            },
            buttonsStyling: false,
          })
        })
        .catch(error => {
          toast({
            component: ToastificationContent,
            props: {
              title: (error.data && error.data.message) || process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
        .finally(() => {
          savingServiceAgent.value = false
        })
    }

    // Sales Agent - the submitting agent, unlike Service Agent this can also adjust the workflow
    // itself: reassigning a Probationary Agent's order to a confirmed Agent drops the now-
    // inapplicable "Verify/Approve Probationary Agent" steps and, if the workflow hadn't gotten
    // past them yet, advances currentStep past them - all handled server-side, reflected here just
    // by re-rendering the updated order. Confirmed-Agent only, same restriction as Service Agent
    // (see ServiceAgentChangeModal's own results.filter).
    const showSalesAgentModal = ref(false)
    const savingSalesAgent = ref(false)

    const saveSalesAgent = ({ agentId }) => {
      savingSalesAgent.value = true
      store.dispatch('app-sales-orders/updateSalesAgent', { id: salesOrder.value._id, agentId })
        .then(response => {
          Object.assign(salesOrder.value, response.data)
          showSalesAgentModal.value = false
          toast({
            component: ToastificationContent,
            props: {
              title: 'Sales Agent Updated',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
        })
        .catch(error => {
          toast({
            component: ToastificationContent,
            props: {
              title: (error.data && error.data.message) || process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
        .finally(() => {
          savingSalesAgent.value = false
        })
    }

    // Unlike Submission, this is a frozen snapshot - saving here only touches this one order's
    // own fields, never the originating submission or any sibling order.
    const showPurchaserDetailsModal = ref(false)
    const savingPurchaserDetails = ref(false)

    const savePurchaserDetails = formData => {
      savingPurchaserDetails.value = true
      store.dispatch('app-sales-orders/updatePurchaserDetails', { id: salesOrder.value._id, formData })
        .then(response => {
          Object.assign(salesOrder.value, response.data)
          showPurchaserDetailsModal.value = false
          Vue.swal({
            title: 'Purchaser Details Updated',
            html: '',
            // eslint-disable-next-line global-require
            imageUrl: require('@/assets/images/icons/success.png'),
            imageWidth: 80,
            imageHeight: 80,
            imageAlt: 'Custom Icon',
            showCancelButton: false,
            confirmButtonText: 'Okay',
            customClass: {
              confirmButton: 'btn btn-primary mt-2',
            },
            buttonsStyling: false,
          })
        })
        .catch(error => {
          toast({
            component: ToastificationContent,
            props: {
              title: (error.data && error.data.message) || process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
        .finally(() => {
          savingPurchaserDetails.value = false
        })
    }

    // Unlike Submission, this is a frozen snapshot - saving here only touches this one order's
    // own intendedUsers entry, never the originating submission item or any sibling order.
    const showIntendedUserModal = ref(false)
    const savingIntendedUser = ref(false)
    const editingIntendedUser = ref(null)

    const openIntendedUserModal = user => {
      editingIntendedUser.value = user
      showIntendedUserModal.value = true
    }

    const saveIntendedUser = formData => {
      savingIntendedUser.value = true
      store.dispatch('app-sales-orders/updateIntendedUser', {
        id: salesOrder.value._id,
        userId: editingIntendedUser.value._id,
        formData,
      })
        .then(response => {
          Object.assign(salesOrder.value, response.data)
          showIntendedUserModal.value = false
          Vue.swal({
            title: 'Intended User Updated',
            html: '',
            // eslint-disable-next-line global-require
            imageUrl: require('@/assets/images/icons/success.png'),
            imageWidth: 80,
            imageHeight: 80,
            imageAlt: 'Custom Icon',
            showCancelButton: false,
            confirmButtonText: 'Okay',
            customClass: {
              confirmButton: 'btn btn-primary mt-2',
            },
            buttonsStyling: false,
          })
        })
        .catch(error => {
          toast({
            component: ToastificationContent,
            props: {
              title: (error.data && error.data.message) || process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
        .finally(() => {
          savingIntendedUser.value = false
        })
    }

    // Unlike Submission, this is a frozen snapshot - saving here only touches this one order's
    // own authorizedPersons entry, never the originating submission item or any sibling order.
    const showAuthorizedPersonModal = ref(false)
    const savingAuthorizedPerson = ref(false)
    const editingAuthorizedPerson = ref(null)

    const openAuthorizedPersonModal = person => {
      editingAuthorizedPerson.value = person
      showAuthorizedPersonModal.value = true
    }

    const saveAuthorizedPerson = formData => {
      savingAuthorizedPerson.value = true
      store.dispatch('app-sales-orders/updateAuthorizedPerson', {
        id: salesOrder.value._id,
        userId: editingAuthorizedPerson.value._id,
        formData,
      })
        .then(response => {
          Object.assign(salesOrder.value, response.data)
          showAuthorizedPersonModal.value = false
          Vue.swal({
            title: 'Authorised Person Updated',
            html: '',
            // eslint-disable-next-line global-require
            imageUrl: require('@/assets/images/icons/success.png'),
            imageWidth: 80,
            imageHeight: 80,
            imageAlt: 'Custom Icon',
            showCancelButton: false,
            confirmButtonText: 'Okay',
            customClass: {
              confirmButton: 'btn btn-primary mt-2',
            },
            buttonsStyling: false,
          })
        })
        .catch(error => {
          toast({
            component: ToastificationContent,
            props: {
              title: (error.data && error.data.message) || process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
        .finally(() => {
          savingAuthorizedPerson.value = false
        })
    }

    // Unlike Submission, this is a frozen snapshot - saving here only touches this one order's
    // own installment fields, never the originating submission item or any sibling order.
    // installmentAmount is never sent - it's permanently read-only via this modal.
    const showInstallmentModal = ref(false)
    const savingInstallment = ref(false)

    const saveInstallment = formData => {
      savingInstallment.value = true
      store.dispatch('app-sales-orders/updateInstallment', { id: salesOrder.value._id, formData })
        .then(response => {
          Object.assign(salesOrder.value, response.data)
          showInstallmentModal.value = false
          Vue.swal({
            title: 'Installment Details Updated',
            html: '',
            // eslint-disable-next-line global-require
            imageUrl: require('@/assets/images/icons/success.png'),
            imageWidth: 80,
            imageHeight: 80,
            imageAlt: 'Custom Icon',
            showCancelButton: false,
            confirmButtonText: 'Okay',
            customClass: {
              confirmButton: 'btn btn-primary mt-2',
            },
            buttonsStyling: false,
          })
        })
        .catch(error => {
          toast({
            component: ToastificationContent,
            props: {
              title: (error.data && error.data.message) || process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
        .finally(() => {
          savingInstallment.value = false
        })
    }

    // Unlike Submission, this is a frozen snapshot - saving here only touches this one order's
    // own applied promotion set, never the originating submission item or any sibling order.
    const showPromotionsModal = ref(false)
    const savingPromotions = ref(false)

    const savePromotions = formData => {
      savingPromotions.value = true
      store.dispatch('app-sales-orders/updatePromotions', { id: salesOrder.value._id, formData })
        .then(response => {
          Object.assign(salesOrder.value, response.data)
          showPromotionsModal.value = false
          Vue.swal({
            title: 'Discounts Updated',
            html: '',
            // eslint-disable-next-line global-require
            imageUrl: require('@/assets/images/icons/success.png'),
            imageWidth: 80,
            imageHeight: 80,
            imageAlt: 'Custom Icon',
            showCancelButton: false,
            confirmButtonText: 'Okay',
            customClass: {
              confirmButton: 'btn btn-primary mt-2',
            },
            buttonsStyling: false,
          })
        })
        .catch(error => {
          toast({
            component: ToastificationContent,
            props: {
              title: (error.data && error.data.message) || process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
        .finally(() => {
          savingPromotions.value = false
        })
    }

    return {
      salesOrder,
      salesAgentDetail,
      salesAgentType,
      permanentAddress,
      correspondenceAddress,
      orderSubtotal,
      orderTotal,
      paymentStatus,
      otherAttachments,
      matchedTenureTier,
      isDownpaymentBelowMinimum,
      isTenureAboveMaximum,
      pluralizedProductType,
      workflowSteps,
      stepsForStage,
      stepIconStatus,
      isStageDisabled,
      stepIconTooltip,
      currentStepIndex,
      verificationMessage,
      isWorkflowActive,
      isReviewingDept,
      isStepOwnerDept,
      isActionRequired,
      canActOnUnownedStep,
      canReassignBackToOwner,
      canActOnStep,
      canVoidOrderState,
      canVoidOrder,
      workflowStatusLabel,
      attachmentInput,
      uploadingAttachment,
      pendingAttachmentFiles,
      triggerAttachmentUpload,
      onAttachmentFileSelected,
      saveAttachments,
      removingAttachmentId,
      removeSalesOrderAttachment,
      attachmentDescriptionValue,
      savingAttachmentDescription,
      showAttachmentDescriptionModal,
      openAttachmentDescriptionModal,
      saveAttachmentDescription,
      commentInput,
      commentsCard,
      focusCommentBox,
      completingStep,
      completeStep,
      sendingBack,
      sendBackToSalesAgent,
      earlierApplicableSteps,
      revertingStep,
      revertStep,
      assigningBack,
      showAssignBackModal,
      assignBackDepartmentOptions,
      assignBackDepartmentValue,
      assignBackNote,
      assignBackToDept,
      confirmAssignBack,
      reassigningBackToOwner,
      reassignBackToOwner,
      voidingOrder,
      voidSalesOrderAction,
      requestingResign,
      confirmRequestResign,
      confirmRemoveIntendedUser,
      confirmRemoveAuthorizedPerson,
      purchaseOptionLabel,
      needTypeLabel,
      canViewThisAction,
      resolvePaymentStatusVariant,
      resolvePaymentStatusLabel,
      isNricRevealed,
      revealNric,
      hideNric,
      newComment,
      submittingComment,
      submitComment,
      currentUserName,
      resolveInitials,
      getVariant,
      activityLogDesc,
      commentsDesc,
      showRequiredInfoModal,
      savingRequiredInfo,
      saveRequiredInformation,
      showServiceAgentModal,
      savingServiceAgent,
      saveServiceAgent,
      showSalesAgentModal,
      savingSalesAgent,
      saveSalesAgent,
      showPurchaserDetailsModal,
      savingPurchaserDetails,
      savePurchaserDetails,
      showIntendedUserModal,
      savingIntendedUser,
      editingIntendedUser,
      openIntendedUserModal,
      saveIntendedUser,
      showAuthorizedPersonModal,
      savingAuthorizedPerson,
      editingAuthorizedPerson,
      openAuthorizedPersonModal,
      saveAuthorizedPerson,
      showInstallmentModal,
      savingInstallment,
      saveInstallment,
      showPromotionsModal,
      savingPromotions,
      savePromotions,
      resolveAcronymName,
    }
  },
}
</script>

<style lang="scss" scoped>
// Overrides a global .particulars_of_deceased table tr th{color:#000000} rule (_phase4bsp3.scss)
// whose nesting depth outweighs .text-danger's single-class specificity - without this, the
// Downpayment/Tenure threshold-violation labels never actually render red.
.particulars_of_deceased {
  th.text-danger,
  td.text-danger {
    color: #ea5455 !important;

    // The info icon's SVG stroke inherits currentColor from this red td - reset it back to the
    // default body text color so only the label/value text turns red, not the icon.
    svg {
      color: #6e6b7b;
    }
  }
}

@media (max-width: 1399px) {
  .content-header-right {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
  }

  .content-header-right .user_block {
    float: none;
    min-width: 0;
    flex: 0 0 calc(50% - 8px);
    margin: 0 8px 8px 0 !important;
  }

  .content-header-right .user_block.file_creation {
    order: 4;
  }

  .content-header-right .user_block.pr_stage_type {
    order: 3;
  }

  .content-header-right .user_block.sign_shape {
    order: 2;
  }

  .content-header-right .user_block.file_payment_type {
    order: 1;
  }
}

@media (max-width: 575px) {
  .content-header-right .user_block {
    flex-basis: 100%;
  }
}

.comments-card {
  ::v-deep .comment-input-plain {
    border: none;
    border-bottom: 1px solid #d8d6de;
    border-radius: 0;
    padding-left: 0;

    &:focus {
      box-shadow: none;
      border-bottom-color: #104d9d;
    }
  }
}

.submission-item-title {
  position: relative !important;
  z-index: 1;
  font-family: Montserrat, Helvetica, Arial, serif;
  font-weight: 500;
  font-size: 28px;
  line-height: 100%;
  letter-spacing: 0px;
  color: #000000;
}
.submission-detail-body {
  padding: 8px 24px 24px;
  margin-bottom: 1rem;
  position: relative;
}
.dashboard__tab-content-card.card {
  gap: 20px;
  padding-top: 20px !important;
  padding-right: 40px !important;
  padding-bottom: 0 !important;
  padding-left: 40px !important;
  margin-bottom: 1rem !important;
}

// Placeholder layout only for everything below - not styled to spec, see the Sales Order
// Workflow scoping memo. Meant to give the designer real markup/classes to style directly.
.so-status-badges {
  gap: 12px;
}
.so-status-card {
  display: flex;
  align-items: center;
  gap: 10px;
}

.so-workflow-bar {
  margin: 0 24px 1.5rem;
}
.so-stepper {
  display: flex;
  align-items: stretch;
  border: 1px solid #e0e5eb;
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 1rem;
}
.so-step {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  flex: 1;
  position: relative;
  background: #ffffff;

  &.active {
    background: #eef3f9;
    font-weight: 600;
  }
  &.done {
    color: #6e6b7b;
  }
  & + .so-step {
    border-left: 1px solid #e0e5eb;
  }
}
.so-step-index {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 1px solid #c8d3dd;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  flex: none;
}
.so-step.active .so-step-index {
  background: #1e3a5f;
  border-color: #1e3a5f;
  color: #ffffff;
}
.so-step-label {
  white-space: nowrap;
}
.so-step-icons {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: auto;
}
.so-step-icon {
  width: 14px;
  height: 14px;
}
.so-step-chevron {
  position: absolute;
  right: -10px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;
  background: #ffffff;
  border-radius: 50%;
}

.so-verification-banner {
  display: flex;
  align-items: center;
  background: #fdf1d9;
  color: #7a5a1a;
  border-radius: 6px;
  padding: 10px 16px;
  font-weight: 600;
  margin-bottom: 1rem;
}

.so-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
}
.so-toolbar-cluster {
  display: flex;
  align-items: center;
  gap: 8px;
}
.so-tool-btn {
  padding: 0.5rem 0.75rem;
}
// Sizing/position for this class come from the pre-existing global rule
// (assets/scss/includes/_phase4bsp3.scss) - it's an absolutely-positioned box, so it isn't a flex
// item and align-self has no effect on it. Only centering the toggle button inside that box
// belongs here, scoped to this component.
.so-tabs-more-btn {
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>

<style lang="scss">
// Figma shows just dots for each comment, no connecting line between them - unlike the Activity
// Logs timeline below, which keeps the default AppTimelineItem line (both share the same
// app-timeline component/markup, so this must stay scoped to .comments-card, not touch
// ul.app-timeline globally). The border-left itself is killed inline in the template (see
// app-timeline-item's style binding) - what's left here is a SEPARATE, site-wide rule
// (assets/scss/includes/_phase2.scss `ul.app-timeline .timeline-item:first-child:before`) that
// fakes a line for the first item via a ::before pseudo-element, which an inline style can't
// reach - has to be suppressed via a real stylesheet rule instead.
.comments-card .timeline-item {
  &:after {
    content: none !important;
  }

  &:first-child:before {
    content: none !important;
  }
}

.dashboard__tabs.submission-detail-tabs .nav-pills .nav-item {
  width: 25%;
}
.dashboard__tabs.submission-detail-tabs .dashboard__tab-card {
  opacity: 1;
  background-color: #FFFFFF;
}
.dashboard__tabs.submission-detail-tabs .nav-pills .nav-link.active .dashboard__tab-card {
  background-color: #F8F8F8;
}
.dashboard__tabs.submission-detail-tabs .nav-pills .nav-link .b-avatar svg {
  margin-right: 0;
}
.dashboard__tab-content-card .purchaser-tabs .nav-tabs .nav-link.active {
  background-color: transparent;
}
.dashboard__tab-content-card .card.has-padding .card-body {
  padding: 0 !important;
}
.order-info-card {
  margin-top: 1.5rem;
  margin-bottom: 3rem;
}
.payment-info-top-row {
  margin-top: 1.5rem;
  margin-bottom: 0;
}
.content-body .card.has-padding .payment-history-table .table {
  th,
  td {
    padding: 1rem 0.75rem !important;
    vertical-align: middle;
  }
}
.dashboard__tabs.submission-detail-tabs .nav-pills .nav-link .feather-dollar-sign {
  border: none !important;
}
.singpass-badge {
  padding: 4px;
  background-color: #D7E5FF !important;
  border: 1px solid #D7E5FF;
  border-radius: 4px;
  opacity: 1;
  color: rgba(75, 108, 183, 1) !important;
  font-family: Open Sans;
  font-weight: 600 !important;
  font-size: 8px;
  line-height: 6px !important;
  letter-spacing: 0.25px;
  text-align: center;
}
.tab-bottom-card {
  margin-bottom: 3rem !important;
}
.sales-action-dropdown.dropdown-toggle::after {
    right: 0px;
    left: 0.714rem;
    margin-right: 5px;
}
</style>
